import type React from 'react';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    Button,
    Badge,
    Input,
    Label,
    Switch,
    Separator,
    Avatar,
    AvatarFallback,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
    Checkbox,
    Skeleton,
} from '@ui';
import { cn } from '@ui/lib/utils';
import {
    FileText,
    Plus,
    Eye,
    Edit,
    Trash2,
    PenTool,
    Save,
    Download,
    Eraser,
    Upload,
    CheckCircle2,
    AlertCircle,
    FileType2,
    FileSignature,
    X,
    Send,
    Users,
    Search,
    History,
    Clock,
    RefreshCw,
    User,
    PawPrint,
    Loader2,
    ChevronRight,
    Image,
} from 'lucide-react';
import { RichTextEditor } from '@ui/components/RichTextEditor';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableQuestionItem } from './SortableQuestionItem';
import { SignatureCanvas } from './SignatureCanvas';
import { useGetGroupMemberPetPage } from '@hooks/generated/group';
import { useGetVersion, useGetTemplateDetail, useGetVersionHistoryList } from '@hooks/generated/consent';
import { toastMessage } from '@ui/components/Toast';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClientManager } from '@app/stores/api-manager';
import type {
    ConsentTemplate,
    ConsentTemplateVersion,
    SignedConsent,
    CustomerConsentGroup,
    TemplateFormData,
    StoreInfo,
    MockCustomer,
    MockPet,
    QuestionItem,
    QuestionType,
} from '../types';

// 무한 스크롤에서 사용하는 고객 타입
interface CustomerListItem {
    id: string;
    name: string;
    phone: string;
    email?: string;
    petCount?: number;
}

// ==================== Template Create/Edit Modal ====================
interface TemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'create' | 'edit';
    formData: TemplateFormData;
    setFormData: React.Dispatch<React.SetStateAction<TemplateFormData>>;
    onSave: () => void;
    storeInfo: StoreInfo;
    previewRef: React.RefObject<HTMLDivElement | null>;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    sensors: any;
    onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onClearFileUpload: () => void;
    onDragEnd: (event: DragEndEvent) => void;
    onAddQuestion: () => void;
    onDeleteQuestion: (id: string) => void;
    onUpdateQuestion: (id: string, updates: Partial<QuestionItem>) => void;
    onQuestionTypeChange: (id: string, type: QuestionType) => void;
    onAddOption: (questionId: string) => void;
    onDeleteOption: (questionId: string, optionId: string) => void;
    onUpdateOption: (questionId: string, optionId: string, value: string) => void;
    onDownloadPdf: () => void;
}

export function TemplateModal({
    isOpen,
    onClose,
    mode,
    formData,
    setFormData,
    onSave,
    storeInfo,
    previewRef,
    fileInputRef,
    sensors,
    onFileUpload,
    onClearFileUpload,
    onDragEnd,
    onAddQuestion,
    onDeleteQuestion,
    onUpdateQuestion,
    onQuestionTypeChange,
    onAddOption,
    onDeleteOption,
    onUpdateOption,
    onDownloadPdf,
}: TemplateModalProps) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[95vw] w-[1400px] max-h-[95vh] overflow-hidden p-0">
                <div className="flex flex-col h-full max-h-[95vh]">
                    {/* Header */}
                    <div className="px-6 py-4 border-b">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-orange-500" />
                                {mode === 'create' ? '새 동의서 양식 추가' : '동의서 양식 수정'}
                            </DialogTitle>
                            <DialogDescription>
                                고객에게 서명받을 동의서 양식을 {mode === 'create' ? '작성' : '수정'}합니다.
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    {/* Main Content - 2 Column Layout */}
                    <div className="flex-1 flex overflow-hidden">
                        {/* Left Side - Form */}
                        <div className="w-1/2 border-r overflow-y-auto p-6 space-y-6">
                            {/* 파일 업로드 또는 직접 작성 선택 */}
                            <div className="flex items-center gap-4 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border">
                                <div className="flex-1">
                                    <Label className="text-base font-semibold">동의서 작성 방식</Label>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">직접 작성하거나 동의서 파일(PDF/이미지)을 업로드할 수 있습니다.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        ref={fileInputRef as React.RefObject<HTMLInputElement>}
                                        accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp"
                                        onChange={onFileUpload}
                                        className="hidden"
                                    />
                                    {formData.isFileUploaded ? (
                                        <div className="flex items-center gap-2">
                                            <Badge variant="secondary" className="gap-1">
                                                {formData.fileType === 'pdf' ? (
                                                    <FileText className="h-3 w-3" />
                                                ) : (
                                                    <FileType2 className="h-3 w-3" />
                                                )}
                                                {formData.fileName}
                                            </Badge>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-danger-500"
                                                onClick={onClearFileUpload}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <Upload className="h-4 w-4 mr-2" />
                                            파일 업로드
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* 기본 정보 섹션 */}
                            <div className="space-y-2">
                                <Label htmlFor="template-name">양식명 *</Label>
                                <Input
                                    id="template-name"
                                    placeholder="예: 위탁 돌봄 동의서"
                                    maxLength={200}
                                    value={formData.name}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                />
                            </div>

                            {/* 파일이 업로드되지 않은 경우에만 직접 편집 가능 */}
                            {!formData.isFileUploaded && (
                                <>
                                    {/* 고정 항목 안내 */}
                                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                                        <div className="flex items-start gap-3">
                                            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <h4 className="text-sm font-semibold text-blue-900">고정 항목 안내</h4>
                                                <p className="text-xs text-blue-700 mt-1">
                                                    매장정보, 보호자 및 반려동물 정보는 동의서 상단에 자동으로 표시됩니다.<br />
                                                    매장정보는 현재 지점 정보에서, 보호자/반려동물 정보는 서명 요청 시 자동으로 입력됩니다.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 요청 항목 (질문 빌더) 섹션 */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <Label className="text-base font-semibold">요청 항목</Label>
                                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">고객에게 추가로 요청할 정보를 설정합니다. 드래그하여 순서를 변경할 수 있습니다.</p>
                                            </div>
                                        </div>

                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={onDragEnd}
                                        >
                                            <SortableContext
                                                items={formData.questions.map((q) => q.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <div className="space-y-3">
                                                    {formData.questions.map((question) => (
                                                        <SortableQuestionItem
                                                            key={question.id}
                                                            question={question}
                                                            onUpdate={onUpdateQuestion}
                                                            onDelete={onDeleteQuestion}
                                                            onTypeChange={onQuestionTypeChange}
                                                            onAddOption={onAddOption}
                                                            onDeleteOption={onDeleteOption}
                                                            onUpdateOption={onUpdateOption}
                                                        />
                                                    ))}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full border-dashed gap-2"
                                            onClick={onAddQuestion}
                                        >
                                            <Plus className="h-4 w-4" />
                                            질문 추가하기
                                        </Button>
                                    </div>

                                    {/* 동의서 내용 섹션 */}
                                    <div className="space-y-2">
                                        <Label>동의서 내용 *</Label>
                                        <RichTextEditor
                                            content={formData.content}
                                            onChange={(content) => setFormData((prev) => ({ ...prev, content }))}
                                            placeholder="동의서 본문을 작성하세요..."
                                            minHeight="300px"
                                        />
                                    </div>
                                </>
                            )}

                            {/* 파일 업로드 시 안내 및 미리보기 */}
                            {formData.isFileUploaded && formData.fileUrl && (
                                <div className="space-y-4">
                                    <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800 border border-dashed text-center">
                                        {formData.fileType === 'pdf' ? (
                                            <FileText className="h-8 w-8 mx-auto text-red-400 mb-2" />
                                        ) : (
                                            <FileType2 className="h-8 w-8 mx-auto text-neutral-400 dark:text-neutral-500 mb-2" />
                                        )}
                                        <h4 className="text-base font-semibold text-neutral-700 dark:text-neutral-200">
                                            {formData.fileType === 'pdf' ? 'PDF 업로드됨' : '이미지 업로드됨'}
                                        </h4>
                                        <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">
                                            동의서 {formData.fileType === 'pdf' ? 'PDF' : '이미지'}가 업로드되었습니다. 오른쪽 미리보기에서 확인하세요.
                                        </p>
                                    </div>

                                    {/* 서명란 별도 제공 옵션 */}
                                    <div className="flex items-center justify-between rounded-lg border p-4 bg-orange-50 border-orange-200">
                                        <div className="flex items-start gap-3">
                                            <PenTool className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <Label htmlFor="signature-box" className="text-orange-900 font-semibold">서명란 별도 제공</Label>
                                                <p className="text-xs text-orange-700 mt-1">
                                                    파일 업로드 시 하단에 서명란을 자동으로 추가합니다.
                                                </p>
                                            </div>
                                        </div>
                                        <Switch
                                            id="signature-box"
                                            checked={formData.hasSignatureBox ?? true}
                                            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, hasSignatureBox: checked }))}
                                        />
                                    </div>

                                    {/* 업로드된 파일 미리보기 (폼 영역) */}
                                    <div className="border rounded-lg overflow-hidden bg-white dark:bg-neutral-900">
                                        <div className="p-2 bg-neutral-100 dark:bg-neutral-800 border-b">
                                            <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                                                업로드된 {formData.fileType === 'pdf' ? 'PDF' : '이미지'} 미리보기
                                            </p>
                                        </div>
                                        <div className="p-4 flex justify-center">
                                            {formData.fileType === 'pdf' ? (
                                                <iframe
                                                    src={formData.fileUrl}
                                                    title="PDF 미리보기"
                                                    className="w-full h-[300px] border rounded"
                                                />
                                            ) : (
                                                <img
                                                    src={formData.fileUrl}
                                                    alt="동의서 이미지 미리보기"
                                                    className="max-w-full max-h-[300px] object-contain rounded border"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 필수/선택 상태 */}
                            <div className="flex items-center justify-between rounded-lg border p-4">
                                <div>
                                    <Label htmlFor="template-mandatory">필수 동의서</Label>
                                    <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">필수로 설정된 양식은 반드시 서명을 받아야 합니다.</p>
                                </div>
                                <Switch
                                    id="template-mandatory"
                                    checked={formData.isMandatory}
                                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isMandatory: checked }))}
                                />
                            </div>
                        </div>

                        {/* Right Side - Preview */}
                        <div className="w-1/2 bg-neutral-100 dark:bg-neutral-800/50 overflow-y-auto p-6">
                            <div className="flex items-center justify-between mb-4">
                                <Label className="text-base font-semibold">동의서 미리보기</Label>
                                {!formData.isFileUploaded && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={onDownloadPdf}
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        PDF 다운로드
                                    </Button>
                                )}
                            </div>

                            {/* 파일 업로드 시 PDF/이미지로 표시 */}
                            {formData.isFileUploaded && formData.fileUrl ? (
                                <div className="space-y-4">
                                    {/* 업로드된 파일 미리보기 */}
                                    <div className="rounded-lg border bg-white dark:bg-neutral-900 overflow-hidden">
                                        {formData.fileType === 'pdf' ? (
                                            <iframe
                                                src={formData.fileUrl}
                                                title="PDF 동의서 미리보기"
                                                className="w-full h-[500px]"
                                            />
                                        ) : (
                                            <div className="p-4">
                                                <img
                                                    src={formData.fileUrl}
                                                    alt="동의서 미리보기"
                                                    className="w-full h-auto object-contain"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* 서명란 미리보기 (hasSignatureBox가 true일 때) */}
                                    {formData.hasSignatureBox && (
                                        <div className="rounded-lg border bg-white dark:bg-neutral-900 p-6">
                                            <div className="border-t-2 border-neutral-300 dark:border-neutral-600 pt-4">
                                                <div className="text-center mb-4">
                                                    <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                                        위 내용을 확인하였으며, 동의합니다.
                                                    </p>
                                                </div>
                                                <div className="flex items-end justify-between">
                                                    <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                                        <p>{new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="border-2 border-dashed border-orange-300 rounded-lg p-4 bg-orange-50 min-w-[200px]">
                                                            <div className="flex flex-col items-center gap-2">
                                                                <PenTool className="h-6 w-6 text-orange-400" />
                                                                <p className="text-xs text-orange-600">서명 영역</p>
                                                            </div>
                                                        </div>
                                                        <p className="text-sm font-semibold mt-2 text-neutral-700 dark:text-neutral-200">
                                                            <span className="text-orange-500">[보호자명]</span> (인)
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div ref={previewRef as React.RefObject<HTMLDivElement>} className="rounded-lg border p-6 bg-white dark:bg-neutral-900 shadow-sm">
                                    {/* 동의서 제목 */}
                                    <h3 className="text-xl font-bold text-center mb-6 pb-4 border-b-2">
                                        {formData.name || '동의서 제목'}
                                    </h3>

                                    {/* 매장정보 + 보호자/반려동물 정보 */}
                                    <div className="mb-6 grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">매장정보</h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-20 shrink-0">매장명:</span> <span>{storeInfo.name}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">주소:</span> <span className="break-all">{storeInfo.address}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">전화번호:</span> <span>{storeInfo.phone}</span></div>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">보호자 및 반려동물 <span className="text-danger-500">*</span></h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-20 shrink-0">보호자:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">연락처:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">반려동물:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">종/품종:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 추가 정보 */}
                                    {formData.questions.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">추가 정보</h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-3">
                                                {formData.questions.map((q) => (
                                                    <div key={q.id} className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded">
                                                        <div className="font-medium">
                                                            {q.name || '질문명'}
                                                            {q.isRequired && <span className="text-danger-500 ml-1">*</span>}
                                                            {q.type === 'multiple' && <span className="text-xs text-blue-500 ml-1">(복수 선택)</span>}
                                                        </div>
                                                        {q.type === 'single' && q.options.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {q.options.map((o) => (
                                                                    <span key={o.id} className="px-3 py-1 bg-white dark:bg-neutral-900 border rounded-full text-xs">
                                                                        ○ {o.value || '옵션'}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {q.type === 'multiple' && q.options.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {q.options.map((o) => (
                                                                    <span key={o.id} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                                                                        ☐ {o.value || '옵션'}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {q.type === 'text' && (
                                                            <div className="mt-2 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900">
                                                                <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                <div className="h-6"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* 동의서 본문 */}
                                    <div className="mb-6">
                                        {formData.content ? (
                                            <div
                                                className="prose prose-sm max-w-none
                                                    [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6
                                                    [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5
                                                    [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-4
                                                    [&_p]:mb-3 [&_p]:leading-relaxed
                                                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                                                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                                                    [&_li]:mb-1
                                                    [&_strong]:font-bold
                                                    [&_em]:italic
                                                    [&_u]:underline
                                                    [&_s]:line-through
                                                    [&_hr]:my-4 [&_hr]:border-neutral-300 dark:border-neutral-600
                                                    text-sm text-neutral-700 dark:text-neutral-200"
                                                dangerouslySetInnerHTML={{ __html: formData.content }}
                                            />
                                        ) : (
                                            <div className="p-8 text-center text-neutral-400 dark:text-neutral-500 border-2 border-dashed rounded-lg">
                                                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">왼쪽에서 동의서 내용을 작성해주세요.</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* 서명란 */}
                                    <div className="pt-6 border-t-2 mt-6">
                                        <div className="text-right">
                                            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                                                위 내용을 확인하였으며, 동의합니다.
                                            </p>
                                            <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                                {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                            <p className="text-lg font-semibold mt-2">
                                                <span className="text-orange-500">[보호자명]</span> (인)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t flex justify-end gap-2">
                        <Button variant="outline" onClick={onClose}>
                            취소
                        </Button>
                        <Button onClick={() => onSave()}>
                            <Save className="mr-2 h-4 w-4" />
                            {mode === 'create' ? '생성하기' : '저장하기'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ==================== Template View Modal ====================
interface TemplateViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    template: ConsentTemplate | null;
    onEdit: (template: ConsentTemplate) => void;
    storeInfo?: StoreInfo;
}

export function TemplateViewModal({ isOpen, onClose, template, onEdit, storeInfo: _storeInfo }: TemplateViewModalProps) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-orange-500" />
                        {template?.name}
                    </DialogTitle>
                </DialogHeader>
                <div className="py-4 space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                            variant={template?.isMandatory ? 'default' : 'secondary'}
                            className={template?.isMandatory ? 'bg-orange-100 text-orange-700' : ''}
                        >
                            {template?.isMandatory ? '필수' : '선택'}
                        </Badge>
                        {template?.isFileUploaded && (
                            <Badge
                                variant="outline"
                                className={cn(
                                    'text-xs gap-1',
                                    template.fileType === 'pdf'
                                        ? 'border-red-200 bg-red-50 text-red-700'
                                        : 'border-blue-200 bg-blue-50 text-blue-700'
                                )}
                            >
                                {template.fileType === 'pdf' ? (
                                    <>
                                        <FileType2 className="h-3 w-3" />
                                        PDF 양식
                                    </>
                                ) : (
                                    <>
                                        <FileSignature className="h-3 w-3" />
                                        이미지 양식
                                    </>
                                )}
                            </Badge>
                        )}
                        <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                            마지막 수정: {template?.updatedAt && new Date(new Date(template.updatedAt).getTime() + 9 * 60 * 60 * 1000).toLocaleString('ko-KR', { timeZone: 'UTC' })}
                        </span>
                    </div>

                    {/* PDF/이미지 업로드 양식인 경우 */}
                    {template?.isFileUploaded && template?.fileUrl ? (
                        <div className="space-y-4">
                            {/* 업로드된 파일 미리보기 */}
                            <div className="rounded-lg border bg-white dark:bg-neutral-900 overflow-hidden">
                                <div className="p-2 bg-neutral-100 dark:bg-neutral-800 border-b">
                                    <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                                        업로드된 {template.fileType === 'pdf' ? 'PDF' : '이미지'} 미리보기
                                    </p>
                                </div>
                                {template.fileType === 'pdf' ? (
                                    <iframe
                                        src={template.fileUrl}
                                        title="PDF 동의서 미리보기"
                                        className="w-full h-[500px]"
                                    />
                                ) : (
                                    <div className="p-4 flex justify-center bg-neutral-50 dark:bg-neutral-800">
                                        <img
                                            src={template.fileUrl}
                                            alt="동의서 미리보기"
                                            className="max-w-full max-h-[500px] object-contain rounded border"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* 서명란 미리보기 (hasSignatureBox가 true일 때) */}
                            {template.hasSignatureBox && (
                                <div className="rounded-lg border bg-white dark:bg-neutral-900 p-6">
                                    <div className="border-t-2 border-neutral-300 dark:border-neutral-600 pt-4">
                                        <div className="text-center mb-4">
                                            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                                위 내용을 확인하였으며, 동의합니다.
                                            </p>
                                        </div>
                                        <div className="flex items-end justify-between">
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                                <p>{new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                            </div>
                                            <div className="text-right">
                                                <div className="border-2 border-dashed border-orange-300 rounded-lg p-4 bg-orange-50 min-w-[200px]">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <PenTool className="h-6 w-6 text-orange-400" />
                                                        <p className="text-xs text-orange-600">서명 영역</p>
                                                    </div>
                                                </div>
                                                <p className="text-sm font-semibold mt-2 text-neutral-700 dark:text-neutral-200">
                                                    <span className="text-orange-500">[보호자명]</span> (인)
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* 템플릿 양식인 경우 - TemplateModal과 동일한 미리보기 */
                        <div className="rounded-lg border p-6 bg-white dark:bg-neutral-900 shadow-sm">
                            {/* 동의서 제목 */}
                            <h3 className="text-xl font-bold text-center mb-6 pb-4 border-b-2">
                                {template?.name || '동의서 제목'}
                            </h3>

                            {/* 매장정보 + 보호자/반려동물 정보 */}
                            <div className="mb-6 grid grid-cols-2 gap-4">
                                <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                    <h4 className="text-sm font-semibold mb-3 text-neutral-900">매장정보</h4>
                                    <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                        <div className="flex"><span className="font-medium w-20 shrink-0">매장명:</span> <span>[매장명]</span></div>
                                        <div className="flex"><span className="font-medium w-20 shrink-0">주소:</span> <span className="break-all">[매장 주소]</span></div>
                                        <div className="flex"><span className="font-medium w-20 shrink-0">전화번호:</span> <span>[전화번호]</span></div>
                                    </div>
                                </div>

                                <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                    <h4 className="text-sm font-semibold mb-3 text-neutral-900">보호자 및 반려동물 <span className="text-danger-500">*</span></h4>
                                    <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                        <div className="flex"><span className="font-medium w-20 shrink-0">보호자:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                        <div className="flex"><span className="font-medium w-20 shrink-0">연락처:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                        <div className="flex"><span className="font-medium w-20 shrink-0">반려동물:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                        <div className="flex"><span className="font-medium w-20 shrink-0">종/품종:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                    </div>
                                </div>
                            </div>

                            {/* 추가 정보 */}
                            {template?.questions && template.questions.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="text-sm font-semibold mb-3 text-neutral-900">추가 정보</h4>
                                    <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-3">
                                        {template.questions.map((q) => (
                                            <div key={q.id} className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded">
                                                <div className="font-medium">
                                                    {q.name || '질문명'}
                                                    {q.isRequired && <span className="text-danger-500 ml-1">*</span>}
                                                    {q.type === 'multiple' && <span className="text-xs text-blue-500 ml-1">(복수 선택)</span>}
                                                </div>
                                                {q.type === 'single' && q.options.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {q.options.map((o) => (
                                                            <span key={o.id} className="px-3 py-1 bg-white dark:bg-neutral-900 border rounded-full text-xs">
                                                                ○ {o.value || '옵션'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {q.type === 'multiple' && q.options.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {q.options.map((o) => (
                                                            <span key={o.id} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                                                                ☐ {o.value || '옵션'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {q.type === 'text' && (
                                                    <div className="mt-2 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900">
                                                        <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                        <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                        <div className="h-6"></div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 동의서 본문 */}
                            <div className="mb-6">
                                {template?.content ? (
                                    <div
                                        className="prose prose-sm max-w-none
                                            [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6
                                            [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5
                                            [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-4
                                            [&_p]:mb-3 [&_p]:leading-relaxed
                                            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                                            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                                            [&_li]:mb-1
                                            [&_strong]:font-bold
                                            [&_em]:italic
                                            [&_u]:underline
                                            [&_s]:line-through
                                            [&_hr]:my-4 [&_hr]:border-neutral-300 dark:border-neutral-600
                                            text-sm text-neutral-700 dark:text-neutral-200"
                                        dangerouslySetInnerHTML={{ __html: template.content }}
                                    />
                                ) : (
                                    <div className="p-8 text-center text-neutral-400 dark:text-neutral-500 border-2 border-dashed rounded-lg">
                                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">동의서 내용이 없습니다.</p>
                                    </div>
                                )}
                            </div>

                            {/* 서명란 */}
                            <div className="pt-6 border-t-2 mt-6">
                                <div className="text-right">
                                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                                        위 내용을 확인하였으며, 동의합니다.
                                    </p>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                        {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </p>
                                    <p className="text-lg font-semibold mt-2">
                                        <span className="text-orange-500">[보호자명]</span> (인)
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose}>
                        닫기
                    </Button>
                    <Button onClick={() => {
                        onClose();
                        if (template) onEdit(template);
                    }}>
                        <Edit className="mr-2 h-4 w-4" />
                        수정하기
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== Template Delete Modal ====================
interface TemplateDeleteModalProps {
    isOpen: boolean;
    onClose: () => void;
    template: ConsentTemplate | null;
    onDelete: () => void;
}

export function TemplateDeleteModal({ isOpen, onClose, template, onDelete }: TemplateDeleteModalProps) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-danger-500">
                        <AlertCircle className="h-5 w-5" />
                        동의서 양식 삭제
                    </DialogTitle>
                </DialogHeader>
                <div className="py-4">
                    <p className="text-neutral-600 dark:text-neutral-300">
                        <span className="font-bold text-danger-500">"{template?.name}"</span> 양식을 삭제하시겠습니까?
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-2">
                        이 작업은 되돌릴 수 없습니다. 이미 서명된 동의서는 영향받지 않습니다.
                    </p>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose}>
                        취소
                    </Button>
                    <Button
                        variant="default"
                        className="bg-danger-500 hover:bg-danger-600"
                        onClick={onDelete}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        삭제하기
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== Signature Select Step Component ====================
interface SignatureSelectStepProps {
    formData: { customerId: string; petId: string; templateId: string };
    setFormData: React.Dispatch<React.SetStateAction<{ customerId: string; petId: string; templateId: string }>>;
    customers: MockCustomer[]; // 미사용 - 무한 스크롤로 대체
    activeTemplates: ConsentTemplate[];
    templates: ConsentTemplate[];
    groupId: number;
    onSignedCustomerCheck?: (isSigned: boolean) => void;
}

function SignatureSelectStep({
    formData,
    setFormData,
    customers: _customers, // 미사용 - 무한 스크롤로 대체
    activeTemplates,
    templates,
    groupId,
    onSignedCustomerCheck,
}: SignatureSelectStepProps) {
    const [customerSearch, setCustomerSearch] = useState('');
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const customerListRef = useRef<HTMLDivElement>(null);

    // 선택된 템플릿 상세 정보 조회 (콘텐츠 포함)
    const { data: templateDetailData } = useGetTemplateDetail({
        request: {
            groupId,
            templateId: formData.templateId ? Number(formData.templateId) : 0,
        },
        options: {
            enabled: !!groupId && !!formData.templateId,
        },
    });
    // API 응답 타입 (ConsentTemplateResponse)에서 필드명 매핑
    // API: contentHtml, title, required vs Local: content, name, isMandatory
    // API QuestionItem: questionText, type, options (string[]), required, displayOrder
    const templateDetail = useMemo(() => {
        if (!templateDetailData) return undefined;
        const apiData = templateDetailData as {
            id?: number;
            title?: string;
            contentHtml?: string;
            contentType?: string;
            fileUrl?: string;
            questions?: Array<{ questionText?: string; type?: string; required?: boolean; displayOrder?: number; options?: Array<string> }>;
            version?: number;
            required?: boolean;
            active?: boolean;
        };
        return {
            id: apiData.id || 0,
            name: apiData.title || '',
            content: apiData.contentHtml || '',
            contentType: apiData.contentType,
            isFileUploaded: apiData.contentType === 'PDF' || apiData.contentType === 'IMAGE',
            fileUrl: apiData.fileUrl,
            fileType: apiData.contentType === 'PDF' ? 'pdf' : apiData.contentType === 'IMAGE' ? 'image' : undefined,
            questions: apiData.questions?.map((q, index) => ({
                id: String(q.displayOrder ?? index),
                name: q.questionText || '',
                type: q.type === 'SINGLE_CHOICE' ? 'single' as const : q.type === 'MULTI_CHOICE' ? 'multi' as const : 'text' as const,
                isRequired: q.required || false,
                options: q.options?.map((optionText, optIndex) => ({ id: String(optIndex), value: optionText })) || [],
                displayOrder: q.displayOrder ?? index,
            })) || [],
            isMandatory: apiData.required || false,
            version: apiData.version,
            createdAt: '',
            updatedAt: '',
        } as ConsentTemplate;
    }, [templateDetailData]);

    // 선택된 템플릿에 대한 서명 완료 요청 조회 (SIGNED 상태)
    const { data: signedRequestsData } = useInfiniteQuery({
        queryKey: ['getSignedRequests', groupId, formData.templateId, 'signature-check'],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await apiClientManager.apis.consentRequest.getRequestPageByStatus({
                groupId: groupId!,
                status: 'SIGNED',
                pageable: { page: pageParam, size: 100 },
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage?.data) {
                return lastPage.data.last ? undefined : (lastPage.data.pageNumber ?? 0) + 1;
            }
            return undefined;
        },
        initialPageParam: 0,
        enabled: !!groupId && !!formData.templateId,
    });

    // 서명 완료한 고객 ID Set
    const signedCustomerIds = useMemo(() => {
        const signedIds = new Set<string>();
        const requests = signedRequestsData?.pages.flatMap((page) => page?.data?.content || []) || [];
        requests.forEach((request) => {
            const requestAny = request as Record<string, unknown>;
            // 선택된 템플릿의 서명만 체크
            if (String(requestAny.templateId) === formData.templateId && requestAny.userId) {
                signedIds.add(String(requestAny.userId));
            }
        });
        return signedIds;
    }, [signedRequestsData, formData.templateId]);

    // 고객 목록 무한 스크롤 (CheckInOutModal 참고)
    const {
        data: customersData,
        fetchNextPage: fetchNextCustomerPage,
        hasNextPage: hasNextCustomerPage,
        isFetchingNextPage: isFetchingNextCustomerPage,
        isLoading: isLoadingCustomers,
    } = useInfiniteQuery({
        queryKey: ['getGroupMemberPage', groupId, customerSearch, 'CUSTOMER', 'signature'],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await apiClientManager.apis.group.getGroupMemberPage({
                groupId: groupId!,
                pageable: {
                    page: pageParam,
                    size: 20,
                },
                role: 'CUSTOMER',
                name: customerSearch || undefined,
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage?.data) {
                return lastPage.data.last ? undefined : (lastPage.data.pageNumber ?? 0) + 1;
            }
            return undefined;
        },
        initialPageParam: 0,
        enabled: !!groupId,
    });

    // 모든 고객 데이터 합치기 및 변환
    const allCustomers = useMemo((): CustomerListItem[] => {
        const content = customersData?.pages.flatMap((page) => page?.data?.content || []) || [];
        return content.map((member): CustomerListItem => ({
            id: String(member.userId || ''),
            name: member.name || '',
            phone: member.phoneNumber || '',
            email: member.email || '',
            petCount: member.petCount,
        }));
    }, [customersData]);

    // 고객 스크롤 핸들러
    const handleCustomerListScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const target = e.currentTarget;
            const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
            if (isBottom && hasNextCustomerPage && !isFetchingNextCustomerPage) {
                fetchNextCustomerPage();
            }
        },
        [hasNextCustomerPage, isFetchingNextCustomerPage, fetchNextCustomerPage]
    );

    // 선택된 고객의 반려동물 목록 조회
    const { data: petsData, isLoading: isLoadingPets } = useGetGroupMemberPetPage({
        request: {
            groupId,
            userId: formData.customerId,
            pageable: { page: 0, size: 50 },
        },
        options: {
            enabled: !!groupId && !!formData.customerId,
        },
    });

    // 반려동물 목록 변환
    const customerPets = useMemo(() => {
        const content = petsData?.content || [];
        return content.map((pet) => {
            const petAny = pet as Record<string, unknown>;
            // species가 객체인 경우 name 필드 추출
            const speciesValue = petAny.species;
            const speciesName = typeof speciesValue === 'object' && speciesValue !== null
                ? (speciesValue as Record<string, unknown>).name as string || ''
                : (speciesValue as string) || '';
            // breed가 객체인 경우 name 필드 추출
            const breedValue = petAny.breed;
            const breedName = typeof breedValue === 'object' && breedValue !== null
                ? (breedValue as Record<string, unknown>).name as string || ''
                : (breedValue as string) || '';
            return {
                id: String(petAny.petId || petAny.id || ''),
                name: (petAny.name as string) || '',
                species: speciesName,
                breed: breedName,
            };
        });
    }, [petsData]);

    // 선택된 고객
    const selectedCustomer = useMemo(
        () => allCustomers.find(c => c.id === formData.customerId),
        [allCustomers, formData.customerId]
    );

    // 선택된 반려동물
    const selectedPet = useMemo(
        () => customerPets.find(p => p.id === formData.petId),
        [customerPets, formData.petId]
    );

    // 고객 선택 핸들러
    const handleCustomerSelect = useCallback((customerId: string) => {
        setFormData(prev => ({ ...prev, customerId, petId: '' }));
        setCustomerSearch('');
    }, [setFormData]);

    // 고객 선택 해제
    const handleCustomerClear = useCallback(() => {
        setFormData(prev => ({ ...prev, customerId: '', petId: '' }));
    }, [setFormData]);

    // 선택된 고객이 서명했는지 확인하고 콜백 호출
    const isSelectedCustomerSigned = useMemo(() => {
        if (!formData.customerId || !formData.templateId) return false;
        return signedCustomerIds.has(formData.customerId);
    }, [formData.customerId, formData.templateId, signedCustomerIds]);

    // 서명 여부 콜백 전달
    useEffect(() => {
        onSignedCustomerCheck?.(isSelectedCustomerSigned);
    }, [isSelectedCustomerSigned, onSignedCustomerCheck]);

    return (
        <div className="space-y-4 py-4">
            {/* 1. 동의서 양식 선택 (첫 번째) - Card 기반 UI */}
            <div className="space-y-2">
                <Label>동의서 양식 <span className="text-danger-500">*</span></Label>
                <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                    {activeTemplates.length === 0 ? (
                        <div className="p-4 text-center text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">활성화된 동의서 양식이 없습니다</p>
                            <p className="text-xs mt-1">먼저 양식을 추가해주세요</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {activeTemplates.map((template) => {
                                const isTemplateSelected = String(template.id) === formData.templateId;
                                return (
                                    <div
                                        key={template.id}
                                        className={cn(
                                            'flex items-center gap-3 p-3 cursor-pointer transition-colors',
                                            isTemplateSelected && 'bg-orange-50 dark:bg-orange-950/30 border-l-2 border-l-orange-500',
                                            !isTemplateSelected && 'hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800'
                                        )}
                                        onClick={() => setFormData(prev => ({ ...prev, templateId: String(template.id) }))}
                                    >
                                        {/* 썸네일 아이콘 */}
                                        <div
                                            className={cn(
                                                'flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0',
                                                template.isMandatory ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-neutral-100 dark:bg-neutral-800'
                                            )}
                                        >
                                            {template.isFileUploaded ? (
                                                template.fileType === 'pdf' ? (
                                                    <FileType2 className="h-5 w-5 text-red-500" />
                                                ) : (
                                                    <Image className="h-5 w-5 text-blue-500" />
                                                )
                                            ) : (
                                                <FileText className={cn(
                                                    'h-5 w-5',
                                                    template.isMandatory ? 'text-orange-600' : 'text-neutral-400 dark:text-neutral-500'
                                                )} />
                                            )}
                                        </div>

                                        {/* 템플릿 정보 */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-medium text-sm text-neutral-800 dark:text-neutral-100 truncate">{template.name}</span>
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        'text-[10px] px-1.5 py-0 h-4',
                                                        template.isFileUploaded
                                                            ? (template.fileType === 'pdf' ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700')
                                                            : 'border-green-200 bg-green-50 text-green-700'
                                                    )}
                                                >
                                                    {template.isFileUploaded
                                                        ? (template.fileType === 'pdf' ? 'PDF' : '이미지')
                                                        : '템플릿'}
                                                </Badge>
                                            </div>
                                            <p className="text-[10px] text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5">
                                                v{template.currentVersion || 1}
                                                {template.isMandatory && <span className="ml-1.5 text-orange-500">• 필수</span>}
                                            </p>
                                        </div>

                                        {/* 선택 표시 */}
                                        {isTemplateSelected && (
                                            <CheckCircle2 className="h-5 w-5 text-orange-500 flex-shrink-0" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* 선택된 양식 미리보기 */}
            {formData.templateId && (() => {
                const selectedTemplate = templates.find((t) => String(t.id) === formData.templateId);
                if (!selectedTemplate) return null;

                return (
                    <div className="space-y-2">
                        <div className="rounded-lg border bg-neutral-50 dark:bg-neutral-800/50 p-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-neutral-800 dark:text-neutral-100">{selectedTemplate.name}</h4>
                                    <span className={cn(
                                        'text-xs px-2 py-0.5 rounded-full font-medium',
                                        selectedTemplate.isFileUploaded
                                            ? (selectedTemplate.fileType === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700')
                                            : 'bg-green-100 text-green-700'
                                    )}>
                                        {selectedTemplate.isFileUploaded
                                            ? (selectedTemplate.fileType === 'pdf' ? 'PDF' : '이미지')
                                            : '템플릿'}
                                    </span>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowTemplatePreview(!showTemplatePreview)}
                                    className="text-xs gap-1"
                                >
                                    <Eye className="h-3 w-3" />
                                    {showTemplatePreview ? '미리보기 닫기' : '미리보기'}
                                </Button>
                            </div>
                        </div>

                        {/* 확장된 미리보기 - TemplateViewModal과 동일한 형식 */}
                        {showTemplatePreview && (
                            <div className="border rounded-lg bg-white dark:bg-neutral-900 overflow-hidden max-h-[400px] overflow-y-auto">
                                {selectedTemplate.isFileUploaded && selectedTemplate.fileUrl ? (
                                    /* PDF/이미지 업로드 양식인 경우 */
                                    <div className="space-y-4">
                                        {/* 업로드된 파일 미리보기 */}
                                        <div className="rounded-lg border bg-white dark:bg-neutral-900 overflow-hidden">
                                            <div className="p-2 bg-neutral-100 dark:bg-neutral-800 border-b">
                                                <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                                                    업로드된 {selectedTemplate.fileType === 'pdf' ? 'PDF' : '이미지'} 미리보기
                                                </p>
                                            </div>
                                            {selectedTemplate.fileType === 'pdf' ? (
                                                <iframe
                                                    src={selectedTemplate.fileUrl}
                                                    title="PDF 동의서 미리보기"
                                                    className="w-full h-[300px]"
                                                />
                                            ) : (
                                                <div className="p-4 flex justify-center bg-neutral-50 dark:bg-neutral-800">
                                                    <img
                                                        src={selectedTemplate.fileUrl}
                                                        alt={selectedTemplate.name}
                                                        className="max-w-full max-h-[300px] object-contain rounded border"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* 서명란 미리보기 (hasSignatureBox가 true일 때) */}
                                        {selectedTemplate.hasSignatureBox && (
                                            <div className="rounded-lg border bg-white dark:bg-neutral-900 p-4">
                                                <div className="border-t-2 border-neutral-300 dark:border-neutral-600 pt-4">
                                                    <div className="text-center mb-3">
                                                        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                                            위 내용을 확인하였으며, 동의합니다.
                                                        </p>
                                                    </div>
                                                    <div className="flex items-end justify-between">
                                                        <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                                            <p>{new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="border-2 border-dashed border-orange-300 rounded-lg p-3 bg-orange-50 min-w-[150px]">
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <PenTool className="h-5 w-5 text-orange-400" />
                                                                    <p className="text-xs text-orange-600">서명 영역</p>
                                                                </div>
                                                            </div>
                                                            <p className="text-sm font-semibold mt-2 text-neutral-700 dark:text-neutral-200">
                                                                <span className="text-orange-500">[보호자명]</span> (인)
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* 템플릿 양식인 경우 - TemplateViewModal과 동일한 미리보기 */
                                    <div className="p-6 bg-white dark:bg-neutral-900">
                                        {/* 동의서 제목 */}
                                        <h3 className="text-lg font-bold text-center mb-4 pb-3 border-b-2">
                                            {selectedTemplate.name || '동의서 제목'}
                                        </h3>

                                        {/* 매장정보 + 보호자/반려동물 정보 */}
                                        <div className="mb-4 grid grid-cols-2 gap-3">
                                            <div className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                                <h4 className="text-xs font-semibold mb-2 text-neutral-900">매장정보</h4>
                                                <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                    <div className="flex"><span className="font-medium w-16 shrink-0">매장명:</span> <span>[매장명]</span></div>
                                                    <div className="flex"><span className="font-medium w-16 shrink-0">주소:</span> <span className="break-all">[매장 주소]</span></div>
                                                    <div className="flex"><span className="font-medium w-16 shrink-0">전화번호:</span> <span>[전화번호]</span></div>
                                                </div>
                                            </div>

                                            <div className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                                <h4 className="text-xs font-semibold mb-2 text-neutral-900">보호자 및 반려동물 <span className="text-danger-500">*</span></h4>
                                                <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                    <div className="flex"><span className="font-medium w-16 shrink-0">보호자:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem]"></span></div>
                                                    <div className="flex"><span className="font-medium w-16 shrink-0">연락처:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem]"></span></div>
                                                    <div className="flex"><span className="font-medium w-16 shrink-0">반려동물:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem]"></span></div>
                                                    <div className="flex"><span className="font-medium w-16 shrink-0">종/품종:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem]"></span></div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 추가 정보 */}
                                        {((templateDetail?.questions || selectedTemplate.questions)?.length ?? 0) > 0 && (
                                            <div className="mb-4">
                                                <h4 className="text-xs font-semibold mb-2 text-neutral-900">추가 정보</h4>
                                                <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-2">
                                                    {(templateDetail?.questions || selectedTemplate.questions || []).map((q) => (
                                                        <div key={q.id} className="p-2 bg-neutral-50 dark:bg-neutral-800 rounded">
                                                            <div className="font-medium">
                                                                {q.name || '질문명'}
                                                                {q.isRequired && <span className="text-danger-500 ml-1">*</span>}
                                                                {q.type === 'multiple' && <span className="text-[10px] text-blue-500 ml-1">(복수 선택)</span>}
                                                            </div>
                                                            {q.type === 'single' && q.options.length > 0 && (
                                                                <div className="mt-1 flex flex-wrap gap-1">
                                                                    {q.options.map((o) => (
                                                                        <span key={o.id} className="px-2 py-0.5 bg-white dark:bg-neutral-900 border rounded-full text-[10px]">
                                                                            ○ {o.value || '옵션'}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {q.type === 'multiple' && q.options.length > 0 && (
                                                                <div className="mt-1 flex flex-wrap gap-1">
                                                                    {q.options.map((o) => (
                                                                        <span key={o.id} className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-[10px]">
                                                                            ☐ {o.value || '옵션'}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {q.type === 'text' && (
                                                                <div className="mt-1 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900">
                                                                    <div className="h-4 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                    <div className="h-4"></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* 동의서 본문 */}
                                        <div className="mb-4">
                                            {(templateDetail?.content || selectedTemplate.content) ? (
                                                <div
                                                    className="prose prose-sm max-w-none
                                                        [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
                                                        [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3
                                                        [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-3
                                                        [&_p]:mb-2 [&_p]:leading-relaxed
                                                        [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
                                                        [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
                                                        [&_li]:mb-1
                                                        [&_strong]:font-bold
                                                        [&_em]:italic
                                                        [&_u]:underline
                                                        [&_s]:line-through
                                                        [&_hr]:my-3 [&_hr]:border-neutral-300 dark:border-neutral-600
                                                        text-xs text-neutral-700 dark:text-neutral-200"
                                                    dangerouslySetInnerHTML={{ __html: templateDetail?.content || selectedTemplate.content || '' }}
                                                />
                                            ) : (
                                                <div className="p-6 text-center text-neutral-400 dark:text-neutral-500 border-2 border-dashed rounded-lg">
                                                    <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
                                                    <p className="text-xs">동의서 내용이 없습니다.</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* 서명란 */}
                                        <div className="pt-4 border-t-2 mt-4">
                                            <div className="text-right">
                                                <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-3">
                                                    위 내용을 확인하였으며, 동의합니다.
                                                </p>
                                                <p className="text-xs text-neutral-600 dark:text-neutral-300">
                                                    {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </p>
                                                <p className="text-sm font-semibold mt-2">
                                                    <span className="text-orange-500">[보호자명]</span> (인)
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* 2. 고객 선택 (두 번째) */}
            <div className="space-y-2">
                <Label>고객 선택 <span className="text-danger-500">*</span></Label>

                {!formData.templateId ? (
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 p-3 border rounded-lg bg-neutral-50 dark:bg-neutral-800/50 text-center">
                        먼저 동의서 양식을 선택해주세요.
                    </p>
                ) : selectedCustomer ? (
                    // 선택된 고객 표시
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-orange-50 border-orange-200">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-orange-100 text-orange-600">
                                    <User className="h-5 w-5" />
                                </AvatarFallback>
                            </Avatar>
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">{selectedCustomer.name}</p>
                                    {isSelectedCustomerSigned && (
                                        <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 h-4">
                                            서명완료
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">{selectedCustomer.phone}</p>
                            </div>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleCustomerClear}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ) : (
                    // 고객 검색 및 선택 목록
                    <div className="space-y-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                            <Input
                                placeholder="고객명 또는 전화번호로 검색..."
                                className="pl-10"
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                            />
                        </div>
                        <div
                            ref={customerListRef}
                            className="max-h-48 overflow-y-auto border rounded-lg"
                            onScroll={handleCustomerListScroll}
                        >
                            {isLoadingCustomers ? (
                                <div className="p-4 space-y-2">
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                </div>
                            ) : allCustomers.length === 0 ? (
                                <div className="p-4 text-center text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                    {customerSearch ? '검색 결과가 없습니다.' : '등록된 고객이 없습니다.'}
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {allCustomers.map((customer) => {
                                        const hasSigned = formData.templateId && signedCustomerIds.has(customer.id);
                                        return (
                                            <button
                                                key={customer.id}
                                                type="button"
                                                onClick={() => handleCustomerSelect(customer.id)}
                                                className={cn(
                                                    'w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800 transition-colors text-left',
                                                    hasSigned && 'bg-green-50/50 dark:bg-green-950/30'
                                                )}
                                            >
                                                <Avatar className="h-9 w-9">
                                                    <AvatarFallback className={cn(
                                                        'text-sm',
                                                        hasSigned ? 'bg-green-100 text-green-600' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300'
                                                    )}>
                                                        {hasSigned ? <CheckCircle2 className="h-4 w-4" /> : customer.name.charAt(0)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium text-sm truncate">{customer.name}</p>
                                                        {hasSigned && (
                                                            <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 h-4">
                                                                서명완료
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 truncate">{customer.phone}</p>
                                                </div>
                                                <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">{customer.petCount ?? 0}마리</span>
                                            </button>
                                        );
                                    })}
                                    {/* 로딩 인디케이터 */}
                                    {isFetchingNextCustomerPage && (
                                        <div className="flex justify-center py-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        </div>
                                    )}
                                    {hasNextCustomerPage && !isFetchingNextCustomerPage && (
                                        <div className="p-2 text-center">
                                            <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                스크롤하여 더 보기
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
}

// ==================== Signature Request Modal ====================
interface SignatureModalProps {
    isOpen: boolean;
    onClose: () => void;
    step: 'select' | 'sign' | 'preview';
    setStep: (step: 'select' | 'sign' | 'preview') => void;
    formData: { customerId: string; petId: string; templateId: string };
    setFormData: React.Dispatch<React.SetStateAction<{ customerId: string; petId: string; templateId: string }>>;
    signatureDataUrl: string;
    signatureCanvasRef: React.RefObject<HTMLCanvasElement | null>;
    activeTemplates: ConsentTemplate[];
    customers: MockCustomer[];
    templates: ConsentTemplate[];
    getSelectedInfo: () => { customer?: MockCustomer; pet?: MockPet; template?: ConsentTemplate };
    onNextStep: () => void;
    startDrawing: (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => void;
    draw: (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => void;
    stopDrawing: () => void;
    clearSignature: () => void;
    onComplete: () => void;
    groupId: number;
    questionAnswers?: Record<string, string | number | string[]>;
    setQuestionAnswers?: React.Dispatch<React.SetStateAction<Record<string, string | number | string[]>>>;
    storeInfo?: StoreInfo;
}

export function SignatureModal({
    isOpen,
    onClose,
    step,
    setStep,
    formData,
    setFormData,
    signatureDataUrl,
    signatureCanvasRef,
    activeTemplates,
    customers,
    templates,
    getSelectedInfo,
    onNextStep,
    startDrawing,
    draw,
    stopDrawing,
    clearSignature,
    onComplete,
    groupId,
    questionAnswers = {},
    setQuestionAnswers,
    storeInfo,
}: SignatureModalProps) {
    // 선택된 고객이 이미 서명했는지 추적
    const [isCustomerSigned, setIsCustomerSigned] = useState(false);

    // 선택된 템플릿 상세 정보 조회 (sign step에서 questions 표시용)
    const { data: templateDetailData } = useGetTemplateDetail({
        request: {
            groupId,
            templateId: formData.templateId ? Number(formData.templateId) : 0,
        },
        options: {
            enabled: !!groupId && !!formData.templateId && step !== 'select',
        },
    });

    // API 응답 타입에서 로컬 타입으로 변환
    const templateDetail = useMemo(() => {
        if (!templateDetailData) return undefined;
        const apiData = templateDetailData as {
            id?: number;
            title?: string;
            contentHtml?: string;
            contentType?: string;
            fileUrl?: string;
            questions?: Array<{ questionText?: string; type?: string; required?: boolean; displayOrder?: number; options?: Array<string> }>;
            version?: number;
            required?: boolean;
            active?: boolean;
        };
        return {
            id: apiData.id || 0,
            name: apiData.title || '',
            content: apiData.contentHtml || '',
            contentType: apiData.contentType,
            isFileUploaded: apiData.contentType === 'PDF' || apiData.contentType === 'IMAGE',
            fileUrl: apiData.fileUrl,
            fileType: apiData.contentType === 'PDF' ? 'pdf' : apiData.contentType === 'IMAGE' ? 'image' : undefined,
            questions: apiData.questions?.map((q, index) => ({
                id: String(q.displayOrder ?? index),
                name: q.questionText || '',
                type: q.type === 'SINGLE_CHOICE' ? 'single' as const : q.type === 'MULTI_CHOICE' ? 'multi' as const : 'text' as const,
                isRequired: q.required || false,
                options: q.options?.map((optionText, optIndex) => ({ id: String(optIndex), value: optionText })) || [],
                displayOrder: q.displayOrder ?? index,
            })) || [],
            isMandatory: apiData.required || false,
            version: apiData.version,
        };
    }, [templateDetailData]);

    // questionAnswer 핸들러
    const handleQuestionAnswerChange = useCallback((questionId: string, value: string | number | string[]) => {
        setQuestionAnswers?.((prev) => ({
            ...prev,
            [questionId]: value,
        }));
    }, [setQuestionAnswers]);

    // 다음 단계로 진행 핸들러 (이미 서명한 고객인 경우 경고)
    const handleNextStep = useCallback(() => {
        if (step === 'select' && isCustomerSigned) {
            toastMessage({
                message: '이미 서명이 완료된 고객입니다. 서명을 다시 요청하시겠습니까?',
                type: 'warning',
            });
        }
        onNextStep();
    }, [step, isCustomerSigned, onNextStep]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PenTool className="h-5 w-5 text-orange-500" />
                        {step === 'select' && '서명 요청'}
                        {step === 'sign' && '전자 서명'}
                        {step === 'preview' && '서명 확인'}
                    </DialogTitle>
                    <DialogDescription>
                        {step === 'select' && '고객, 반려동물, 동의서 양식을 선택해주세요.'}
                        {step === 'sign' && '아래 영역에 서명해주세요.'}
                        {step === 'preview' && '서명 내용을 확인해주세요.'}
                    </DialogDescription>
                </DialogHeader>

                {/* Step 1: Select */}
                {step === 'select' && (
                    <SignatureSelectStep
                        formData={formData}
                        setFormData={setFormData}
                        customers={customers}
                        activeTemplates={activeTemplates}
                        templates={templates}
                        groupId={groupId}
                        onSignedCustomerCheck={setIsCustomerSigned}
                    />
                )}

                {/* Step 2: Sign */}
                {step === 'sign' && (
                    <div className="space-y-4 py-4">
                        <div className="rounded-lg border bg-neutral-50 dark:bg-neutral-800/50 p-3">
                            <div className="grid grid-cols-3 gap-2 text-sm">
                                <div>
                                    <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">고객:</span>
                                    <span className="ml-1 font-medium">{getSelectedInfo().customer?.name}</span>
                                </div>

                                <div>
                                    <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">양식:</span>
                                    <span className="ml-1 font-medium">{getSelectedInfo().template?.name}</span>
                                </div>
                            </div>
                        </div>

                        {/* 추가 질문 (Questions) */}
                        {templateDetail?.questions && templateDetail.questions.length > 0 && (
                            <div className="space-y-3">
                                <Label className="text-sm font-semibold">추가 항목</Label>
                                <div className="space-y-3">
                                    {templateDetail.questions.map((question) => (
                                        <div key={question.id} className="rounded-lg border bg-white dark:bg-neutral-900 p-3">
                                            <div className="mb-2">
                                                <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                                                    {question.name}
                                                </span>
                                                {question.isRequired && (
                                                    <span className="text-danger-500 ml-1">*</span>
                                                )}
                                                {question.type === 'multi' && (
                                                    <span className="text-xs text-blue-500 ml-1">(복수 선택 가능)</span>
                                                )}
                                            </div>
                                            {/* 단일 선택형 - 라디오 버튼 */}
                                            {question.type === 'single' && question.options.length > 0 && (
                                                <div className="space-y-1">
                                                    {question.options.map((option) => (
                                                        <label
                                                            key={option.id}
                                                            className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800"
                                                        >
                                                            <input
                                                                type="radio"
                                                                name={`question-${question.id}`}
                                                                value={option.id}
                                                                checked={questionAnswers[question.id] === option.id}
                                                                onChange={() => handleQuestionAnswerChange(question.id, option.id)}
                                                                className="w-4 h-4 text-orange-500 focus:ring-orange-500"
                                                            />
                                                            <span className="text-sm text-neutral-700 dark:text-neutral-200">{option.value}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                            {/* 복수 선택형 - 체크박스 */}
                                            {question.type === 'multi' && question.options.length > 0 && (
                                                <div className="space-y-1">
                                                    {question.options.map((option) => {
                                                        const selectedOptions = Array.isArray(questionAnswers[question.id])
                                                            ? (questionAnswers[question.id] as string[])
                                                            : [];
                                                        const isChecked = selectedOptions.includes(option.id);
                                                        return (
                                                            <label
                                                                key={option.id}
                                                                className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-blue-50"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    value={option.id}
                                                                    checked={isChecked}
                                                                    onChange={() => {
                                                                        const newSelected = isChecked
                                                                            ? selectedOptions.filter(id => id !== option.id)
                                                                            : [...selectedOptions, option.id];
                                                                        handleQuestionAnswerChange(question.id, newSelected);
                                                                    }}
                                                                    className="w-4 h-4 text-blue-500 focus:ring-blue-500 rounded"
                                                                />
                                                                <span className="text-sm text-neutral-700 dark:text-neutral-200">{option.value}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {/* 서술형 - 텍스트 입력 */}
                                            {question.type === 'text' && (
                                                <textarea
                                                    placeholder="답변을 입력해주세요..."
                                                    value={String(questionAnswers[question.id] || '')}
                                                    onChange={(e) => handleQuestionAnswerChange(question.id, e.target.value)}
                                                    className="w-full text-sm border rounded-md px-3 py-2 min-h-[60px] resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>서명 영역</Label>
                                <Button variant="outline" size="sm" onClick={clearSignature} className="gap-1">
                                    <Eraser className="h-3 w-3" />
                                    지우기
                                </Button>
                            </div>
                            <div className="rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 p-1">
                                <canvas
                                    ref={signatureCanvasRef as React.RefObject<HTMLCanvasElement>}
                                    width={500}
                                    height={200}
                                    className="w-full cursor-crosshair touch-none"
                                    style={{ maxWidth: '100%', height: 'auto' }}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={startDrawing}
                                    onTouchMove={draw}
                                    onTouchEnd={stopDrawing}
                                />
                            </div>
                            <p className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 text-center">
                                마우스 또는 터치로 서명해주세요
                            </p>
                        </div>
                    </div>
                )}

                {/* Step 3: Preview - SendConsentModal과 동일한 형식 */}
                {step === 'preview' && (
                    <div className="space-y-4 py-4">
                        <div className="border rounded-lg bg-white dark:bg-neutral-900 overflow-hidden max-h-[500px] overflow-y-auto">
                            {/* PDF/이미지 업로드 양식인 경우 */}
                            {templateDetail?.isFileUploaded && templateDetail?.fileUrl ? (
                                <div className="p-6 bg-white dark:bg-neutral-900">
                                    <div className="space-y-4">
                                        <div className="rounded-lg border bg-white dark:bg-neutral-900 overflow-hidden">
                                            <div className="p-2 bg-neutral-100 dark:bg-neutral-800 border-b">
                                                <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                                                    업로드된 {templateDetail.fileType === 'pdf' ? 'PDF' : '이미지'} 미리보기
                                                </p>
                                            </div>
                                            {templateDetail.fileType === 'pdf' ? (
                                                <iframe
                                                    src={templateDetail.fileUrl}
                                                    title="PDF 동의서 미리보기"
                                                    className="w-full h-[200px]"
                                                />
                                            ) : (
                                                <div className="p-4 flex justify-center bg-neutral-50 dark:bg-neutral-800">
                                                    <img
                                                        src={templateDetail.fileUrl}
                                                        alt={templateDetail.name}
                                                        className="max-w-full max-h-[200px] object-contain rounded border"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 매장정보 + 보호자/반려동물 정보 */}
                                    <div className="mt-4 mb-4 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg space-y-3">
                                        <div>
                                            <h4 className="text-xs font-semibold mb-2 text-neutral-900">매장정보</h4>
                                            <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-16 shrink-0">매장명:</span> <span>{storeInfo?.name || '[매장명]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">주소:</span> <span className="break-all">{storeInfo?.address || '[매장 주소]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">전화번호:</span> <span>{storeInfo?.phone || '[전화번호]'}</span></div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-xs font-semibold mb-2 text-neutral-900">보호자 및 반려동물</h4>
                                            <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-16 shrink-0">보호자:</span> <span>{getSelectedInfo().customer?.name || '[보호자명]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">연락처:</span> <span>{getSelectedInfo().customer?.phone || '[연락처]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">반려동물:</span> <span>{getSelectedInfo().pet?.name || '[반려동물명]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">종/품종:</span> <span>{typeof getSelectedInfo().pet?.species === 'object' && getSelectedInfo().pet?.species !== null ? ((getSelectedInfo().pet?.species as unknown) as Record<string, unknown>)?.name as string : getSelectedInfo().pet?.species || '[종/품종]'}</span></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 서명란 (PDF/이미지 양식용) */}
                                    <div className="pt-4 border-t-2 mt-4">
                                        <div className="text-right">
                                            <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-3">
                                                위 내용을 확인하였으며, 동의합니다.
                                            </p>
                                            <p className="text-xs text-neutral-600 dark:text-neutral-300">
                                                {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                            <div className="mt-2 inline-block">
                                                <img
                                                    src={signatureDataUrl}
                                                    alt="서명"
                                                    className="max-h-16 border rounded bg-white dark:bg-neutral-900"
                                                />
                                            </div>
                                            <p className="text-sm font-semibold mt-2">
                                                {getSelectedInfo().customer?.name} (인)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* 템플릿 양식인 경우 */
                                <div className="p-6 bg-white dark:bg-neutral-900">
                                    {/* 동의서 제목 */}
                                    <h3 className="text-lg font-bold text-center mb-4 pb-3 border-b-2">
                                        {templateDetail?.name || getSelectedInfo().template?.name || '동의서 제목'}
                                    </h3>

                                    {/* 매장정보 + 보호자/반려동물 정보 */}
                                    <div className="mb-4 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg space-y-3">
                                        <div>
                                            <h4 className="text-xs font-semibold mb-2 text-neutral-900">매장정보</h4>
                                            <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-16 shrink-0">매장명:</span> <span>{storeInfo?.name || '[매장명]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">주소:</span> <span className="break-all">{storeInfo?.address || '[매장 주소]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">전화번호:</span> <span>{storeInfo?.phone || '[전화번호]'}</span></div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-xs font-semibold mb-2 text-neutral-900">보호자 및 반려동물</h4>
                                            <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-16 shrink-0">보호자:</span> <span>{getSelectedInfo().customer?.name || '[보호자명]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">연락처:</span> <span>{getSelectedInfo().customer?.phone || '[연락처]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">반려동물:</span> <span>{getSelectedInfo().pet?.name || '[반려동물명]'}</span></div>
                                                <div className="flex"><span className="font-medium w-16 shrink-0">종/품종:</span> <span>{typeof getSelectedInfo().pet?.species === 'object' && getSelectedInfo().pet?.species !== null ? ((getSelectedInfo().pet?.species as unknown) as Record<string, unknown>)?.name as string : getSelectedInfo().pet?.species || '[종/품종]'}</span></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 추가 정보 (질문 응답) */}
                                    {templateDetail?.questions && templateDetail.questions.length > 0 && (
                                        <div className="mb-4">
                                            <h4 className="text-xs font-semibold mb-2 text-neutral-900">추가 정보</h4>
                                            <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-2">
                                                {templateDetail.questions.map((q) => {
                                                    const answer = questionAnswers[q.id];
                                                    const selectedOption = q.type === 'single' && q.options?.find(o => o.id === answer);
                                                    return (
                                                        <div key={q.id} className="p-2 bg-neutral-50 dark:bg-neutral-800 rounded">
                                                            <div className="font-medium">
                                                                {q.name}
                                                                {q.isRequired && <span className="text-danger-500 ml-1">*</span>}
                                                            </div>
                                                            <div className="mt-1 text-neutral-700 dark:text-neutral-200">
                                                                {selectedOption ? (
                                                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-[10px]">
                                                                        ● {selectedOption.value}
                                                                    </span>
                                                                ) : answer ? (
                                                                    <span>{String(answer)}</span>
                                                                ) : (
                                                                    <span className="text-neutral-400 dark:text-neutral-500 italic">응답 없음</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* 동의서 본문 */}
                                    <div className="mb-4">
                                        {(templateDetail?.content || getSelectedInfo().template?.content) ? (
                                            <div
                                                className="prose prose-sm max-w-none
                                                    [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
                                                    [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3
                                                    [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-3
                                                    [&_p]:mb-2 [&_p]:leading-relaxed
                                                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
                                                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
                                                    [&_li]:mb-1
                                                    [&_strong]:font-bold
                                                    [&_em]:italic
                                                    [&_u]:underline
                                                    [&_s]:line-through
                                                    [&_hr]:my-3 [&_hr]:border-neutral-300 dark:border-neutral-600
                                                    text-xs text-neutral-700 dark:text-neutral-200"
                                                dangerouslySetInnerHTML={{ __html: templateDetail?.content || getSelectedInfo().template?.content || '' }}
                                            />
                                        ) : (
                                            <div className="p-6 text-center text-neutral-400 dark:text-neutral-500 border-2 border-dashed rounded-lg">
                                                <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
                                                <p className="text-xs">동의서 내용이 없습니다.</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* 서명란 */}
                                    <div className="pt-4 border-t-2 mt-4">
                                        <div className="text-right">
                                            <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-3">
                                                위 내용을 확인하였으며, 동의합니다.
                                            </p>
                                            <p className="text-xs text-neutral-600 dark:text-neutral-300">
                                                {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                            <div className="mt-2 inline-block">
                                                <img
                                                    src={signatureDataUrl}
                                                    alt="서명"
                                                    className="max-h-16 border rounded bg-white dark:bg-neutral-900"
                                                />
                                            </div>
                                            <p className="text-sm font-semibold mt-2">
                                                {getSelectedInfo().customer?.name} (인)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {step === 'select' && (
                        <>
                            <Button variant="outline" onClick={onClose}>
                                취소
                            </Button>
                            <Button onClick={handleNextStep} disabled={activeTemplates.length === 0}>
                                다음
                            </Button>
                        </>
                    )}
                    {step === 'sign' && (
                        <>
                            <Button variant="outline" onClick={() => setStep('select')}>
                                이전
                            </Button>
                            <Button onClick={onNextStep}>
                                서명 확인
                            </Button>
                        </>
                    )}
                    {step === 'preview' && (
                        <>
                            <Button variant="outline" onClick={() => setStep('sign')}>
                                서명 다시하기
                            </Button>
                            <Button onClick={onComplete}>
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                서명 완료
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== Signed Consent Detail Modal ====================
interface SignedConsentDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    consent: SignedConsent | null;
    templates: ConsentTemplate[];
}

export function SignedConsentDetailModal({ isOpen, onClose, consent, templates }: SignedConsentDetailModalProps) {
    // 서명 시점의 동의서 내용을 가져옴 (스냅샷 또는 현재 템플릿)
    const getSignedContent = () => {
        if (!consent) return '';
        // 서명 시점의 스냅샷이 있으면 사용
        if (consent.signedContent) return consent.signedContent;
        // 없으면 현재 템플릿 내용 사용
        return templates.find((t) => String(t.id) === consent.templateId)?.content || '동의서 내용을 불러올 수 없습니다.';
    };

    // 현재 템플릿의 버전과 서명 시점 버전 비교
    const currentTemplate = consent ? templates.find(t => String(t.id) === consent.templateId) : null;
    const isOutdatedVersion = consent?.templateVersion && currentTemplate?.currentVersion && consent.templateVersion < currentTemplate.currentVersion;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSignature className="h-5 w-5 text-orange-500" />
                        서명된 동의서 상세
                    </DialogTitle>
                    <DialogDescription>
                        {consent?.templateName}
                    </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-lg border p-4">
                            <h4 className="text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-2">보호자 정보</h4>
                            <div className="space-y-1">
                                <p className="text-sm">
                                    <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">이름:</span>{' '}
                                    <span className="font-medium">{consent?.customerName}</span>
                                </p>
                                <p className="text-sm">
                                    <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">연락처:</span>{' '}
                                    <span className="font-medium">{consent?.customerPhone}</span>
                                </p>
                            </div>
                        </div>
                        <div className="rounded-lg border p-4">
                            <h4 className="text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-2">반려동물 정보</h4>
                            <div className="space-y-1">
                                <p className="text-sm">
                                    <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">이름:</span>{' '}
                                    <span className="font-medium">{consent?.petName}</span>
                                </p>
                                {consent?.petSpecies && (
                                    <p className="text-sm">
                                        <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">종류:</span>{' '}
                                        <span className="font-medium">{consent.petSpecies}</span>
                                    </p>
                                )}
                                {consent?.petBreed && (
                                    <p className="text-sm">
                                        <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">품종:</span>{' '}
                                        <span className="font-medium">{consent.petBreed}</span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border p-4">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-neutral-600 dark:text-neutral-300">동의서 양식</h4>
                            {/* 버전 정보 표시 */}
                            <div className="flex items-center gap-2">
                                {consent?.templateVersion && (
                                    <Badge variant="outline" className="text-xs gap-1">
                                        <History className="h-3 w-3" />
                                        v{consent.templateVersion}
                                    </Badge>
                                )}
                                {isOutdatedVersion && (
                                    <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                                        이전 버전
                                    </Badge>
                                )}
                            </div>
                        </div>
                        <Badge variant="outline" className="mb-3">
                            {consent?.templateName}
                        </Badge>

                        {/* 이전 버전 알림 */}
                        {isOutdatedVersion && (
                            <div className="mb-3 p-2 rounded bg-amber-50 border border-amber-200">
                                <p className="text-xs text-amber-700 flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" />
                                    현재 동의서 양식은 v{currentTemplate?.currentVersion}입니다. 이 서명은 v{consent?.templateVersion} 기준으로 작성되었습니다.
                                </p>
                            </div>
                        )}

                        {consent && (
                            <div
                                className="prose prose-sm max-w-none text-sm text-neutral-700 dark:text-neutral-200"
                                dangerouslySetInnerHTML={{ __html: getSignedContent() }}
                            />
                        )}
                    </div>

                    <div className="rounded-lg border p-4">
                        <h4 className="text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-2">서명 정보</h4>
                        <div className="flex items-center justify-between">
                            <div className="space-y-1">
                                <p className="text-sm">
                                    <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">서명일시:</span>{' '}
                                    <span className="font-medium">
                                        {consent && new Date(new Date(consent.signedAt).getTime() + 9 * 60 * 60 * 1000).toLocaleString('ko-KR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZone: 'UTC',
                                        })}
                                    </span>
                                </p>
                            </div>
                            <Badge className="bg-success-100 text-success-700">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                서명 완료
                            </Badge>
                        </div>
                        {consent?.signatureImageUrl && (
                            <div className="mt-3 p-3 bg-white dark:bg-neutral-900 border rounded">
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-2">서명 이미지</p>
                                <img
                                    src={consent.signatureImageUrl}
                                    alt="서명"
                                    className="max-h-24 block"
                                    style={{ maxHeight: '96px', display: 'block', width: 'auto', height: 'auto' }}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        닫기
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== Customer Detail Modal ====================
interface CustomerDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerGroup: CustomerConsentGroup | null;
    selectedConsentForPreview: SignedConsent | null;
    onSelectConsent: (consent: SignedConsent) => void;
    onNavigateToCustomer: (customerName: string, customerPhone: string) => void;
    onDownloadPdf: () => void;
    storeInfo: StoreInfo;
    templates: ConsentTemplate[];
    previewConsentRef: React.RefObject<HTMLDivElement | null>;
}

export function CustomerDetailModal({
    isOpen,
    onClose,
    customerGroup,
    selectedConsentForPreview,
    onSelectConsent,
    onNavigateToCustomer,
    onDownloadPdf,
    storeInfo,
    templates,
    previewConsentRef,
}: CustomerDetailModalProps) {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-orange-100 text-orange-700 font-semibold">
                                {customerGroup?.customerName.charAt(0)}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <button
                                type="button"
                                className="text-left hover:text-orange-600 hover:underline transition-colors cursor-pointer"
                                onClick={() => customerGroup && onNavigateToCustomer(customerGroup.customerName, customerGroup.customerPhone)}
                                title="사용자 관리 페이지에서 상세 보기"
                            >
                                {customerGroup?.customerName}
                            </button>
                            <p className="text-sm font-normal text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">{customerGroup?.customerPhone}</p>
                        </div>
                    </DialogTitle>
                    <DialogDescription>
                        총 {customerGroup?.totalConsents}개의 동의서
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-[300px_1fr] gap-4 mt-4 h-[calc(80vh-150px)]">
                    {/* 왼쪽: 동의서 목록 */}
                    <div className="border rounded-lg overflow-hidden flex flex-col">
                        <div className="bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 border-b">
                            <h4 className="font-semibold text-sm">동의서 목록</h4>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {customerGroup?.consents.map((consent) => (
                                <div
                                    key={consent.id}
                                    className={cn(
                                        "p-3 border-b cursor-pointer hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800 transition-colors",
                                        selectedConsentForPreview?.id === consent.id && "bg-orange-50 dark:bg-orange-950/30 border-l-4 border-l-orange-500"
                                    )}
                                    onClick={() => onSelectConsent(consent)}
                                >
                                    <div className="flex items-start gap-2">
                                        <Avatar className="h-8 w-8 shrink-0">
                                            <AvatarFallback className={cn(
                                                "text-xs",
                                                consent.petSpecies === '강아지' ? 'bg-amber-100 text-amber-700' :
                                                    consent.petSpecies === '고양이' ? 'bg-purple-100 text-purple-700' :
                                                        'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200'
                                            )}>
                                                {consent.petSpecies === '강아지' ? '🐕' : consent.petSpecies === '고양이' ? '🐱' : consent.petName.charAt(0)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{consent.templateName}</p>
                                            <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">{consent.petName}</p>
                                            <p className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-1">
                                                {new Date(new Date(consent.signedAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { timeZone: 'UTC' })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 오른쪽: 동의서 미리보기 */}
                    <div className="border rounded-lg overflow-hidden flex flex-col">
                        <div className="bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 border-b flex items-center justify-between">
                            <h4 className="font-semibold text-sm">동의서 미리보기</h4>
                            {selectedConsentForPreview && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={onDownloadPdf}
                                    className="gap-1"
                                >
                                    <Download className="h-4 w-4" />
                                    PDF 다운로드
                                </Button>
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 bg-neutral-100 dark:bg-neutral-800/50">
                            {selectedConsentForPreview ? (
                                <div ref={previewConsentRef as React.RefObject<HTMLDivElement>} className="bg-white dark:bg-neutral-900 rounded-lg shadow p-6">
                                    <h3 className="text-xl font-bold text-center mb-6 pb-4 border-b-2">
                                        {selectedConsentForPreview.templateName}
                                    </h3>

                                    <div className="mb-6 grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">매장정보</h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-20 shrink-0">매장명:</span> <span>{storeInfo.name}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">주소:</span> <span className="break-all">{storeInfo.address}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">전화번호:</span> <span>{storeInfo.phone}</span></div>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">보호자 및 반려동물</h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-20 shrink-0">보호자:</span> <span>{selectedConsentForPreview.customerName}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">연락처:</span> <span>{selectedConsentForPreview.customerPhone}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">반려동물:</span> <span>{selectedConsentForPreview.petName}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">종/품종:</span> <span>{selectedConsentForPreview.petSpecies}{selectedConsentForPreview.petBreed ? ` - ${selectedConsentForPreview.petBreed}` : ''}</span></div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <div
                                            className="prose prose-sm max-w-none text-sm text-neutral-700 dark:text-neutral-200"
                                            dangerouslySetInnerHTML={{
                                                __html: templates.find((t) => String(t.id) === selectedConsentForPreview.templateId)?.content || ''
                                            }}
                                        />
                                    </div>

                                    <div className="pt-6 border-t-2 mt-6">
                                        <div className="text-right">
                                            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                                                위 내용을 확인하였으며, 동의합니다.
                                            </p>
                                            <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                                {new Date(new Date(selectedConsentForPreview.signedAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}
                                            </p>
                                            <p className="text-lg font-semibold mt-2">
                                                {selectedConsentForPreview.customerName} (인)
                                            </p>
                                            {selectedConsentForPreview.signatureImageUrl && (
                                                <div className="mt-3 inline-block p-2 border rounded bg-white dark:bg-neutral-900 signature-container">
                                                    <SignatureCanvas
                                                        signatureDataUrl={selectedConsentForPreview.signatureImageUrl}
                                                        className="signature-canvas"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-full text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                    <div className="text-center">
                                        <FileSignature className="h-12 w-12 mx-auto mb-2" />
                                        <p>동의서를 선택해주세요</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose}>
                        닫기
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== Send Consent Modal ====================
type SendMethod = 'sms' | 'email' | 'kakao';

interface SendHistoryItem {
    id: string;
    customerId: string;
    customerName: string;
    customerPhone?: string;
    templateId: number;
    templateName: string;
    status: string;
    sentAt?: string;
    signedAt?: string;
}

interface SendConsentModalProps {
    isOpen: boolean;
    onClose: () => void;
    templates: ConsentTemplate[];
    mockCustomers: MockCustomer[];
    onSend: (templateId: string, customerIds: string[], version: number, sendMethod: SendMethod) => void;
    groupId?: number;
    onNavigateToCustomer?: (customerId: string, templateId: number) => void;
    storeInfo?: StoreInfo;
}

export function SendConsentModal({
    isOpen,
    onClose,
    templates,
    mockCustomers: _mockCustomers, // 미사용 - 무한 스크롤로 대체
    onSend,
    groupId,
    onNavigateToCustomer,
    storeInfo,
}: SendConsentModalProps) {

    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [selectedVersion, setSelectedVersion] = useState<number>(0);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sendMethod, setSendMethod] = useState<SendMethod>('email');
    const [showPreview, setShowPreview] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    // 선택된 고객의 반려동물 정보 조회 (첫 번째 선택된 고객만)
    const selectedCustomerId = selectedCustomerIds.length > 0 ? selectedCustomerIds[0] : null;
    const { data: customerPetData } = useGetGroupMemberPetPage({
        request: {
            groupId: groupId || 0,
            userId: selectedCustomerId || '',
            pageable: { page: 0, size: 10 },
        },
        options: {
            enabled: isOpen && !!groupId && !!selectedCustomerId && showPreview,
        },
    });

    // 발송 이력 조회 (모든 상태)
    const { data: sendHistoryData, isLoading: isLoadingSendHistory } = useInfiniteQuery({
        queryKey: ['getSendHistory', groupId, 'send-modal-history'],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await apiClientManager.apis.consentRequest.getRequestPage({
                groupId: groupId!,
                pageable: { page: pageParam, size: 50 },
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage?.data) {
                return lastPage.data.last ? undefined : (lastPage.data.pageNumber ?? 0) + 1;
            }
            return undefined;
        },
        initialPageParam: 0,
        enabled: isOpen && !!groupId && showHistory,
    });

    // 발송 이력 데이터 가공
    const sendHistoryItems = useMemo((): SendHistoryItem[] => {
        const requests = sendHistoryData?.pages.flatMap((page) => page?.data?.content || []) || [];
        return requests.map((request) => {
            const req = request as Record<string, unknown>;
            const template = templates.find(t => t.id === req.templateId);
            return {
                id: String(req.id || ''),
                customerId: String(req.userId || req.customerId || ''),
                customerName: String(req.userName || req.customerName || '고객'),
                customerPhone: String(req.userPhone || req.customerPhone || ''),
                templateId: Number(req.templateId) || 0,
                templateName: template?.name || String(req.templateTitle || '동의서'),
                status: String(req.status || 'SENT'),
                sentAt: req.sentAt ? String(req.sentAt) : undefined,
                signedAt: req.signedAt ? String(req.signedAt) : undefined,
            };
        }).sort((a, b) => {
            // 최신순 정렬
            const dateA = a.signedAt || a.sentAt || '';
            const dateB = b.signedAt || b.sentAt || '';
            return dateB.localeCompare(dateA);
        });
    }, [sendHistoryData, templates]);

    // 선택된 템플릿에 대한 서명 완료 요청 조회 (SIGNED 상태)
    const { data: signedRequestsData } = useInfiniteQuery({
        queryKey: ['getSignedRequests', groupId, selectedTemplateId, 'send-modal-check'],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await apiClientManager.apis.consentRequest.getRequestPageByStatus({
                groupId: groupId!,
                status: 'SIGNED',
                pageable: { page: pageParam, size: 100 },
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage?.data) {
                return lastPage.data.last ? undefined : (lastPage.data.pageNumber ?? 0) + 1;
            }
            return undefined;
        },
        initialPageParam: 0,
        enabled: isOpen && !!groupId && !!selectedTemplateId,
    });

    // 서명 완료한 고객 ID Set
    const signedCustomerIds = useMemo(() => {
        const signedIds = new Set<string>();
        const requests = signedRequestsData?.pages.flatMap((page) => page?.data?.content || []) || [];
        requests.forEach((request) => {
            const requestAny = request as Record<string, unknown>;
            // 선택된 템플릿의 서명만 체크
            if (String(requestAny.templateId) === selectedTemplateId && requestAny.userId) {
                signedIds.add(String(requestAny.userId));
            }
        });
        return signedIds;
    }, [signedRequestsData, selectedTemplateId]);

    // 고객 목록 무한 스크롤 (CheckInOutModal 참고)
    const {
        data: customersData,
        fetchNextPage: fetchNextCustomerPage,
        hasNextPage: hasNextCustomerPage,
        isFetchingNextPage: isFetchingNextCustomerPage,
        isLoading: isLoadingCustomers,
    } = useInfiniteQuery({
        queryKey: ['getGroupMemberPage', groupId, searchQuery, 'CUSTOMER'],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await apiClientManager.apis.group.getGroupMemberPage({
                groupId: groupId!,
                pageable: {
                    page: pageParam,
                    size: 20,
                },
                role: 'CUSTOMER',
                name: searchQuery || undefined,
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage?.data) {
                return lastPage.data.last ? undefined : (lastPage.data.pageNumber ?? 0) + 1;
            }
            return undefined;
        },
        initialPageParam: 0,
        enabled: isOpen && !!groupId,
    });

    // 모든 고객 데이터 합치기 및 변환
    const allCustomers = useMemo((): CustomerListItem[] => {
        const content = customersData?.pages.flatMap((page) => page?.data?.content || []) || [];
        return content.map((member): CustomerListItem => ({
            id: String(member.userId || ''),
            name: member.name || '',
            phone: member.phoneNumber || '',
            email: member.email || '',
            petCount: member.petCount || 0,
        }));
    }, [customersData]);

    // 선택된 고객 및 반려동물 정보
    const selectedCustomerInfo = useMemo(() => {
        if (!selectedCustomerId) return null;
        const customer = allCustomers.find(c => c.id === selectedCustomerId);
        if (!customer) return null;

        const pets = (customerPetData as { content?: Array<{ id?: number; name?: string; species?: string; breed?: string }> })?.content || [];
        const firstPet = pets[0];

        return {
            name: customer.name,
            phone: customer.phone,
            petName: firstPet?.name || '',
            petSpecies: firstPet?.species || '',
            petBreed: firstPet?.breed || '',
        };
    }, [selectedCustomerId, allCustomers, customerPetData]);

    // 고객 스크롤 핸들러
    const handleCustomerListScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const target = e.currentTarget;
            const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
            if (isBottom && hasNextCustomerPage && !isFetchingNextCustomerPage) {
                fetchNextCustomerPage();
            }
        },
        [hasNextCustomerPage, isFetchingNextCustomerPage, fetchNextCustomerPage]
    );

    // 선택된 버전의 상세 정보 조회
    const { data: versionDetailData, isLoading: isLoadingVersionDetail } = useGetVersion({
        request: {
            groupId: groupId || 0,
            templateId: selectedTemplateId ? Number(selectedTemplateId) : 0,
            versionNumber: selectedVersion || 1,
        },
        options: {
            enabled: isOpen && !!groupId && !!selectedTemplateId && !!selectedVersion,
        },
    });

    // 버전 상세 정보를 로컬 타입으로 변환
    const versionDetail = useMemo(() => {
        if (!versionDetailData) return null;
        const data = versionDetailData as {
            versionNumber?: number;
            contentHtml?: string;
            questions?: Array<{
                id?: number;
                questionText?: string;
                type?: string;
                options?: Array<{ id?: number; value?: string }>;
                required?: boolean;
            }>;
            fileUrl?: string;
            contentType?: string;
        };

        return {
            content: data.contentHtml || '',
            questions: (data.questions || []).map((q) => ({
                id: String(q.id || ''),
                name: q.questionText || '',
                type: q.type,
                options: (q.options || []).map((o, index) => ({
                    id: index,
                    value: o || '',
                })),
                isRequired: q.required || false,
            })),
            fileUrl: data.fileUrl,
            isFileUploaded: !!data.fileUrl && data.contentType !== 'HTML',
            fileType: data.contentType === 'PDF' ? 'pdf' as const : data.contentType === 'IMAGE' ? 'image' as const : null,
        };
    }, [versionDetailData]);


    // 모든 템플릿 사용 가능 (isMandatory는 필수/선택 여부만 나타냄)
    const activeTemplates = useMemo(() => templates, [templates]);

    // 전송 방법에 따른 고객 가용 여부 확인
    const isCustomerAvailable = useCallback((customer: CustomerListItem) => {
        if (sendMethod === 'sms') {
            return !!customer.phone && customer.phone.trim() !== '';
        }
        if (sendMethod === 'email') {
            return !!customer.email;
        }
        return true;
    }, [sendMethod]);

    // 선택된 템플릿 정보
    const selectedTemplate = useMemo(
        () => templates.find(t => String(t.id) === selectedTemplateId),
        [templates, selectedTemplateId]
    );

    // 선택된 템플릿의 버전 이력 조회
    const { data: versionHistoryData } = useGetVersionHistoryList({
        request: {
            groupId: groupId || 0,
            templateId: selectedTemplateId ? Number(selectedTemplateId) : 0,
        },
        options: {
            enabled: isOpen && !!groupId && !!selectedTemplateId,
        },
    });

    // 템플릿 선택 시 기본 버전 설정
    const handleTemplateChange = (templateId: string) => {
        setSelectedTemplateId(templateId);
        const template = templates.find(t => String(t.id) === templateId);
        if (template) {
            setSelectedVersion(template.activeVersion || template.currentVersion || 1);
        }
    };

    // 선택된 템플릿의 버전 목록 (API에서 조회한 데이터 우선 사용)
    const availableVersions = useMemo(() => {
        // API에서 조회한 버전 이력이 있으면 사용
        if (versionHistoryData && Array.isArray(versionHistoryData) && versionHistoryData.length > 0) {
            return versionHistoryData
                .map((v: { versionNumber?: number; createdAt?: string; changeNote?: string }) => ({
                    version: v.versionNumber || 1,
                    createdAt: v.createdAt || '',
                    changeNote: v.changeNote,
                }))
                .sort((a, b) => b.version - a.version);
        }
        // 폴백: selectedTemplate.versionHistory가 있으면 사용
        if (selectedTemplate?.versionHistory && selectedTemplate.versionHistory.length > 0) {
            return [...selectedTemplate.versionHistory].sort((a, b) => b.version - a.version);
        }
        // 둘 다 없으면 현재 버전만 표시
        if (selectedTemplate) {
            return [{
                version: selectedTemplate.currentVersion || 1,
                createdAt: selectedTemplate.createdAt || '',
                changeNote: undefined,
            }];
        }
        return [];
    }, [versionHistoryData, selectedTemplate]);

    // 고객 선택 토글
    const toggleCustomer = (customerId: string) => {
        setSelectedCustomerIds(prev =>
            prev.includes(customerId)
                ? prev.filter(id => id !== customerId)
                : [...prev, customerId]
        );
    };



    // 선택 가능한 고객만 필터링
    const availableCustomers = useMemo(() => {
        return allCustomers.filter(isCustomerAvailable);
    }, [allCustomers, isCustomerAvailable]);

    // 전송 방법에 따라 정렬된 고객 목록 (전송 가능한 고객 우선)
    const sortedCustomers = useMemo(() => {
        return [...allCustomers].sort((a, b) => {
            const aAvailable = isCustomerAvailable(a);
            const bAvailable = isCustomerAvailable(b);
            // 전송 가능한 고객을 우선 정렬
            if (aAvailable && !bAvailable) return -1;
            if (!aAvailable && bAvailable) return 1;
            // 같은 그룹 내에서는 이름순 정렬
            return a.name.localeCompare(b.name);
        });
    }, [allCustomers, isCustomerAvailable]);

    // 전체 선택/해제 (선택 가능한 고객만)
    const toggleAllCustomers = () => {
        const availableIds = availableCustomers.map(c => c.id);
        const selectedAvailableIds = selectedCustomerIds.filter(id => availableIds.includes(id));

        if (selectedAvailableIds.length === availableIds.length) {
            setSelectedCustomerIds([]);
        } else {
            setSelectedCustomerIds(availableIds);
        }
    };

    // 발송 처리
    const handleSend = () => {
        if (!selectedTemplateId || selectedCustomerIds.length === 0 || !selectedVersion) return;
        onSend(selectedTemplateId, selectedCustomerIds, selectedVersion, sendMethod);
        handleClose();
    };

    // 모달 닫기 시 초기화
    const handleClose = () => {
        setSelectedTemplateId('');
        setSelectedVersion(0);
        setSelectedCustomerIds([]);
        setSearchQuery('');
        setSendMethod('email');
        setShowPreview(false);
        onClose();
    };


    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Send className="h-5 w-5 text-orange-500" />
                        동의서 발송
                    </DialogTitle>
                    <DialogDescription>
                        특정 고객에게 동의서 양식을 발송하여 서명을 요청합니다.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4 py-4">
                    {/* 동의서 양식 선택 - Card 기반 UI */}
                    <div className="space-y-2">
                        <Label>동의서 양식 선택 *</Label>
                        <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                            {activeTemplates.length === 0 ? (
                                <div className="p-4 text-center text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">활성화된 동의서 양식이 없습니다</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {activeTemplates.map((template) => {
                                        const isTemplateSelected = String(template.id) === selectedTemplateId;
                                        return (
                                            <div
                                                key={template.id}
                                                className={cn(
                                                    'flex items-center gap-3 p-3 cursor-pointer transition-colors',
                                                    isTemplateSelected && 'bg-orange-50 dark:bg-orange-950/30 border-l-2 border-l-orange-500',
                                                    !isTemplateSelected && 'hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800'
                                                )}
                                                onClick={() => handleTemplateChange(String(template.id))}
                                            >
                                                {/* 썸네일 아이콘 */}
                                                <div
                                                    className={cn(
                                                        'flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0',
                                                        template.isMandatory ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-neutral-100 dark:bg-neutral-800'
                                                    )}
                                                >
                                                    {template.isFileUploaded ? (
                                                        template.fileType === 'pdf' ? (
                                                            <FileType2 className="h-5 w-5 text-red-500" />
                                                        ) : (
                                                            <Image className="h-5 w-5 text-blue-500" />
                                                        )
                                                    ) : (
                                                        <FileText className={cn(
                                                            'h-5 w-5',
                                                            template.isMandatory ? 'text-orange-600' : 'text-neutral-400 dark:text-neutral-500'
                                                        )} />
                                                    )}
                                                </div>

                                                {/* 템플릿 정보 */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="font-medium text-sm text-neutral-800 dark:text-neutral-100 truncate">{template.name}</span>
                                                        <Badge
                                                            variant="outline"
                                                            className={cn(
                                                                'text-[10px] px-1.5 py-0 h-4',
                                                                template.isFileUploaded
                                                                    ? (template.fileType === 'pdf' ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700')
                                                                    : 'border-green-200 bg-green-50 text-green-700'
                                                            )}
                                                        >
                                                            {template.isFileUploaded
                                                                ? (template.fileType === 'pdf' ? 'PDF' : '이미지')
                                                                : '템플릿'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-[10px] text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5">
                                                        v{template.currentVersion || template.activeVersion || 1}
                                                        {template.isMandatory && <span className="ml-1.5 text-orange-500">• 필수</span>}
                                                    </p>
                                                </div>

                                                {/* 선택 표시 */}
                                                {isTemplateSelected && (
                                                    <CheckCircle2 className="h-5 w-5 text-orange-500 flex-shrink-0" />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        {activeTemplates.length === 0 && (
                            <p className="text-xs text-danger-500">활성화된 동의서 양식이 없습니다.</p>
                        )}
                    </div>

                    {/* 버전 선택 */}
                    {selectedTemplate && availableVersions.length > 0 && (
                        <div className="space-y-2">
                            <Label htmlFor="send-version">전송할 버전 선택</Label>
                            <select
                                id="send-version"
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                value={selectedVersion}
                                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                            >
                                {availableVersions.map((v) => {
                                    const isActive = v.version === selectedTemplate.activeVersion;
                                    const isLatest = v.version === selectedTemplate.currentVersion;
                                    return (
                                        <option key={v.version} value={v.version}>
                                            v{v.version}
                                            {isActive ? ' (사용중)' : ''}
                                            {isLatest && !isActive ? ' (최신)' : ''}
                                        </option>
                                    );
                                })}
                            </select>
                            {selectedVersion !== selectedTemplate.activeVersion && (
                                <p className="text-xs text-amber-600 flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" />
                                    기본 사용 버전(v{selectedTemplate.activeVersion})이 아닌 다른 버전을 선택했습니다.
                                </p>
                            )}
                            {selectedVersion === selectedTemplate.activeVersion && (
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                    기본 사용 버전이 선택되었습니다.
                                </p>
                            )}
                        </div>
                    )}

                    {/* 선택된 양식 정보 및 미리보기 */}
                    {selectedTemplate && (
                        <div className="space-y-2">
                            <div className="rounded-lg border bg-neutral-50 dark:bg-neutral-800/50 p-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-medium text-neutral-800 dark:text-neutral-100">{selectedTemplate.name}</h4>
                                        <Badge variant="outline" className="text-xs">
                                            v{selectedVersion}
                                        </Badge>
                                        <span className={cn(
                                            'text-xs px-2 py-0.5 rounded-full font-medium',
                                            selectedTemplate.isFileUploaded
                                                ? (selectedTemplate.fileType === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700')
                                                : 'bg-green-100 text-green-700'
                                        )}>
                                            {selectedTemplate.isFileUploaded
                                                ? (selectedTemplate.fileType === 'pdf' ? 'PDF' : '이미지')
                                                : '템플릿'}
                                        </span>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowPreview(!showPreview)}
                                        className="text-xs gap-1"
                                    >
                                        <Eye className="h-3 w-3" />
                                        {showPreview ? '미리보기 닫기' : '미리보기'}
                                    </Button>
                                </div>
                            </div>

                            {/* 확장된 미리보기 - TemplateViewModal과 동일한 형식 */}
                            {showPreview && (
                                <div className="border rounded-lg bg-white dark:bg-neutral-900 overflow-hidden max-h-[400px] overflow-y-auto">
                                    {isLoadingVersionDetail ? (
                                        <div className="p-8 text-center text-neutral-400 dark:text-neutral-500">
                                            <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                                            <p className="text-sm">미리보기 로딩 중...</p>
                                        </div>
                                    ) : (versionDetail?.isFileUploaded && versionDetail?.fileUrl) || (selectedTemplate.isFileUploaded && selectedTemplate.fileUrl) ? (
                                        /* PDF/이미지 업로드 양식인 경우 */
                                        <div className="space-y-4">
                                            {/* 업로드된 파일 미리보기 */}
                                            <div className="rounded-lg border bg-white dark:bg-neutral-900 overflow-hidden">
                                                <div className="p-2 bg-neutral-100 dark:bg-neutral-800 border-b">
                                                    <p className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
                                                        업로드된 {(versionDetail?.fileType || selectedTemplate.fileType) === 'pdf' ? 'PDF' : '이미지'} 미리보기
                                                    </p>
                                                </div>
                                                {(versionDetail?.fileType || selectedTemplate.fileType) === 'pdf' ? (
                                                    <iframe
                                                        src={versionDetail?.fileUrl || selectedTemplate.fileUrl || ''}
                                                        title="PDF 동의서 미리보기"
                                                        className="w-full h-[300px]"
                                                    />
                                                ) : (
                                                    <div className="p-4 flex justify-center bg-neutral-50 dark:bg-neutral-800">
                                                        <img
                                                            src={versionDetail?.fileUrl || selectedTemplate.fileUrl || ''}
                                                            alt={selectedTemplate.name}
                                                            className="max-w-full max-h-[300px] object-contain rounded border"
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* 서명란 미리보기 (hasSignatureBox가 true일 때) */}
                                            {selectedTemplate.hasSignatureBox && (
                                                <div className="rounded-lg border bg-white dark:bg-neutral-900 p-4">
                                                    <div className="border-t-2 border-neutral-300 dark:border-neutral-600 pt-4">
                                                        <div className="text-center mb-3">
                                                            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                                                위 내용을 확인하였으며, 동의합니다.
                                                            </p>
                                                        </div>
                                                        <div className="flex items-end justify-between">
                                                            <div className="text-sm text-neutral-600 dark:text-neutral-300">
                                                                <p>{new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="border-2 border-dashed border-orange-300 rounded-lg p-3 bg-orange-50 min-w-[150px]">
                                                                    <div className="flex flex-col items-center gap-1">
                                                                        <PenTool className="h-5 w-5 text-orange-400" />
                                                                        <p className="text-xs text-orange-600">서명 영역</p>
                                                                    </div>
                                                                </div>
                                                                <p className="text-sm font-semibold mt-2 text-neutral-700 dark:text-neutral-200">
                                                                    <span className="text-orange-500">[보호자명]</span> (인)
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        /* 템플릿 양식인 경우 - TemplateViewModal과 동일한 미리보기 */
                                        <div className="p-6 bg-white dark:bg-neutral-900">
                                            {/* 동의서 제목 */}
                                            <h3 className="text-lg font-bold text-center mb-4 pb-3 border-b-2">
                                                {selectedTemplate.name || '동의서 제목'}
                                            </h3>

                                            {/* 매장정보 + 보호자/반려동물 정보 */}
                                            <div className="mb-4 grid grid-cols-2 gap-3">
                                                <div className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                                    <h4 className="text-xs font-semibold mb-2 text-neutral-900">매장정보</h4>
                                                    <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                        <div className="flex"><span className="font-medium w-16 shrink-0">매장명:</span> <span>{storeInfo?.name || '[매장명]'}</span></div>
                                                        <div className="flex"><span className="font-medium w-16 shrink-0">주소:</span> <span className="break-all">{storeInfo?.address || '[매장 주소]'}</span></div>
                                                        <div className="flex"><span className="font-medium w-16 shrink-0">전화번호:</span> <span>{storeInfo?.phone || '[전화번호]'}</span></div>
                                                    </div>
                                                </div>

                                                <div className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                                    <h4 className="text-xs font-semibold mb-2 text-neutral-900">보호자 및 반려동물 <span className="text-danger-500">*</span></h4>
                                                    <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-1">
                                                        <div className="flex"><span className="font-medium w-16 shrink-0">보호자:</span> <span>{selectedCustomerInfo?.name || <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem] text-neutral-400 dark:text-neutral-500">(고객 선택시 표시)</span>}</span></div>
                                                        <div className="flex"><span className="font-medium w-16 shrink-0">연락처:</span> <span>{selectedCustomerInfo?.phone || <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem]"></span>}</span></div>
                                                        <div className="flex"><span className="font-medium w-16 shrink-0">반려동물:</span> <span>{selectedCustomerInfo?.petName || <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem]"></span>}</span></div>
                                                        <div className="flex"><span className="font-medium w-16 shrink-0">종/품종:</span> <span>{selectedCustomerInfo?.petSpecies ? `${selectedCustomerInfo.petSpecies}${selectedCustomerInfo.petBreed ? ` / ${selectedCustomerInfo.petBreed}` : ''}` : <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1rem]"></span>}</span></div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 추가 정보 */}
                                            {(versionDetail?.questions || selectedTemplate.questions)?.length > 0 && (
                                                <div className="mb-4">
                                                    <h4 className="text-xs font-semibold mb-2 text-neutral-900">추가 정보</h4>
                                                    <div className="text-xs text-neutral-600 dark:text-neutral-300 space-y-2">

                                                        {(versionDetail?.questions || selectedTemplate.questions).map((q) => (
                                                            <div key={q.id} className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded">
                                                                <div className="font-medium">
                                                                    {q.name || '질문명'}
                                                                    {q.isRequired && <span className="text-danger-500 ml-1">*</span>}
                                                                    {q.type === 'multiple' && <span className="text-xs text-blue-500 ml-1">(복수 선택)</span>}
                                                                </div>
                                                                {q.type === 'SINGLE_CHOICE' && q.options.length > 0 && (
                                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                                        {q.options.map((o, idx) => (
                                                                            <span key={o.id ?? idx} className="px-3 py-1 bg-white dark:bg-neutral-900 border rounded-full text-xs">
                                                                                ○ {String(o.value || '옵션')}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {q.type === "MULTI_CHOICE" && q.options.length > 0 && (
                                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                                        {q.options.map((o, idx) => (
                                                                            <span key={o.id ?? idx} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                                                                                ☐ {String(o.value || '옵션')}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {q.type === 'TEXT' && (
                                                                    <div className="mt-2 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900">
                                                                        <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                        <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                        <div className="h-6"></div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* 동의서 본문 */}
                                            <div className="mb-4">
                                                {(versionDetail?.content || selectedTemplate.content) ? (
                                                    <div
                                                        className="prose prose-sm max-w-none
                                                            [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
                                                            [&_h2]:text-base [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3
                                                            [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-3
                                                            [&_p]:mb-2 [&_p]:leading-relaxed
                                                            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
                                                            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
                                                            [&_li]:mb-1
                                                            [&_strong]:font-bold
                                                            [&_em]:italic
                                                            [&_u]:underline
                                                            [&_s]:line-through
                                                            [&_hr]:my-3 [&_hr]:border-neutral-300 dark:border-neutral-600
                                                            text-xs text-neutral-700 dark:text-neutral-200"
                                                        dangerouslySetInnerHTML={{ __html: versionDetail?.content || selectedTemplate.content || '' }}
                                                    />
                                                ) : (
                                                    <div className="p-6 text-center text-neutral-400 dark:text-neutral-500 border-2 border-dashed rounded-lg">
                                                        <FileText className="h-6 w-6 mx-auto mb-2 opacity-50" />
                                                        <p className="text-xs">동의서 내용이 없습니다.</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* 서명란 */}
                                            <div className="pt-4 border-t-2 mt-4">
                                                <div className="text-right">
                                                    <p className="text-xs text-neutral-600 dark:text-neutral-300 mb-3">
                                                        위 내용을 확인하였으며, 동의합니다.
                                                    </p>
                                                    <p className="text-xs text-neutral-600 dark:text-neutral-300">
                                                        {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                                    </p>
                                                    <p className="text-sm font-semibold mt-2">
                                                        <span className="text-orange-500">{selectedCustomerInfo?.name || '[보호자명]'}</span> (인)
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 발송 이력 토글 버튼 */}
                    <div className="flex items-center justify-between py-2 border-b">
                        <Button
                            type="button"
                            variant={showHistory ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setShowHistory(!showHistory)}
                            className="gap-2"
                        >
                            <History className="h-4 w-4" />
                            발송 이력 {showHistory ? '숨기기' : '보기'}
                        </Button>
                        {sendHistoryItems.length > 0 && !showHistory && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                총 {sendHistoryItems.length}건의 발송 이력
                            </span>
                        )}
                    </div>

                    {/* 발송 이력 섹션 */}
                    {showHistory && (
                        <div className="space-y-2 border rounded-lg p-3 bg-neutral-50 dark:bg-neutral-800/50">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <History className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
                                    발송 이력
                                </h4>
                                <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                    클릭하여 고객 상세 보기
                                </span>
                            </div>

                            {isLoadingSendHistory ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                </div>
                            ) : sendHistoryItems.length === 0 ? (
                                <div className="py-6 text-center text-neutral-400 dark:text-neutral-500">
                                    <Send className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">발송 이력이 없습니다</p>
                                </div>
                            ) : (
                                <div className="max-h-60 overflow-y-auto space-y-1">
                                    {sendHistoryItems.map((item) => {
                                        const statusConfig = {
                                            SENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: '발송됨' },
                                            VIEWED: { bg: 'bg-amber-100', text: 'text-amber-700', label: '열람' },
                                            SIGNED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '서명완료' },
                                            EXPIRED: { bg: 'bg-neutral-100 dark:bg-neutral-800', text: 'text-neutral-500 dark:text-neutral-400 dark:text-neutral-500', label: '만료' },
                                            CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', label: '취소' },
                                        }[item.status] || { bg: 'bg-neutral-100 dark:bg-neutral-800', text: 'text-neutral-600 dark:text-neutral-300', label: item.status };

                                        return (
                                            <div
                                                key={item.id}
                                                className="flex items-center gap-3 p-2 bg-white dark:bg-neutral-900 rounded-lg border hover:bg-orange-50 dark:hover:bg-orange-950/30 hover:border-orange-200 dark:hover:border-orange-800 cursor-pointer transition-colors"
                                                onClick={() => {
                                                    if (onNavigateToCustomer && item.customerId) {
                                                        onNavigateToCustomer(item.customerId, item.templateId);
                                                        handleClose();
                                                    }
                                                }}
                                            >
                                                <Avatar className="h-8 w-8 shrink-0">
                                                    <AvatarFallback className={cn(
                                                        'text-xs',
                                                        item.status === 'SIGNED' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300'
                                                    )}>
                                                        {item.status === 'SIGNED' ? <CheckCircle2 className="h-4 w-4" /> : item.customerName.charAt(0)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-sm truncate">{item.customerName}</span>
                                                        <span className={cn(
                                                            'px-1.5 py-0.5 rounded text-[10px] font-medium',
                                                            statusConfig.bg, statusConfig.text
                                                        )}>
                                                            {statusConfig.label}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 truncate">
                                                        {item.templateName}
                                                    </p>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                        {item.signedAt
                                                            ? new Date(new Date(item.signedAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { timeZone: 'UTC' })
                                                            : item.sentAt
                                                                ? new Date(new Date(item.sentAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { timeZone: 'UTC' })
                                                                : '-'}
                                                    </p>
                                                    <ChevronRight className="h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 ml-auto" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 전송 방법 선택 */}
                    <div className="space-y-2">
                        <Label>전송 방법</Label>
                        <div className="flex gap-4">

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="sendMethod"
                                    value="email"
                                    checked={sendMethod === 'email'}
                                    onChange={() => setSendMethod('email')}
                                    className="w-4 h-4 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm">이메일</span>
                            </label>
                            {/* <label className="flex items-center gap-2 cursor-pointer text-neutral-400 dark:text-neutral-500">
                                <input
                                    type="radio"
                                    name="sendMethod"
                                    value="sms"
                                    checked={sendMethod === 'sms'}
                                    onChange={() => setSendMethod('sms')}
                                    disabled
                                    className="w-4 h-4 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm">SMS (준비중)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-neutral-400 dark:text-neutral-500">
                                <input
                                    type="radio"
                                    name="sendMethod"
                                    value="kakao"
                                    checked={sendMethod === 'kakao'}
                                    onChange={() => setSendMethod('kakao')}
                                    disabled
                                    className="w-4 h-4"
                                />
                                <span className="text-sm">카카오톡 (준비중)</span>
                            </label> */}
                        </div>
                    </div>

                    {/* 고객 선택 */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>발송 대상 고객 선택 *</Label>
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                {selectedCustomerIds.length}명 선택됨
                            </span>
                        </div>

                        {/* 검색 및 전체 선택 */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                                <Input
                                    placeholder="고객명 또는 전화번호 검색..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={toggleAllCustomers}
                            >
                                {selectedCustomerIds.length === availableCustomers.length ? '전체 해제' : '전체 선택'}
                            </Button>
                        </div>

                        {/* 고객 목록 - 전송 가능한 고객 우선 정렬 */}
                        <div
                            className="border rounded-lg max-h-60 overflow-y-auto"
                            onScroll={handleCustomerListScroll}
                        >
                            {isLoadingCustomers ? (
                                <div className="p-4 space-y-2">
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                    <Skeleton className="h-12 w-full" />
                                </div>
                            ) : sortedCustomers.length === 0 ? (
                                <div className="p-4 text-center text-neutral-400 dark:text-neutral-500">
                                    <Users className="h-8 w-8 mx-auto mb-2" />
                                    <p className="text-sm">{searchQuery ? '검색 결과가 없습니다' : '등록된 고객이 없습니다'}</p>
                                </div>
                            ) : (
                                <>
                                    {sortedCustomers.map((customer) => {
                                        const isAvailable = isCustomerAvailable(customer);
                                        const hasSigned = selectedTemplateId && signedCustomerIds.has(customer.id);
                                        const unavailableReason = !isAvailable
                                            ? (sendMethod === 'sms' ? '전화번호 없음' : '이메일 없음')
                                            : '';

                                        return (
                                            <label
                                                key={customer.id}
                                                className={cn(
                                                    'flex items-center gap-3 p-3 transition-colors border-b last:border-b-0',
                                                    isAvailable ? 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800' : 'cursor-not-allowed opacity-50 bg-neutral-100 dark:bg-neutral-800',
                                                    selectedCustomerIds.includes(customer.id) && isAvailable && 'bg-orange-50 dark:bg-orange-950/30',
                                                    hasSigned && 'bg-green-50/50 dark:bg-green-950/30'
                                                )}
                                            >
                                                <Checkbox
                                                    checked={selectedCustomerIds.includes(customer.id)}
                                                    onCheckedChange={() => isAvailable && toggleCustomer(customer.id)}
                                                    disabled={!isAvailable}
                                                />
                                                <Avatar className="h-9 w-9">
                                                    <AvatarFallback className={cn(
                                                        'text-sm',
                                                        hasSigned ? 'bg-green-100 text-green-600' : (isAvailable ? 'bg-orange-100 text-orange-700' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 dark:text-neutral-500')
                                                    )}>
                                                        {hasSigned ? <CheckCircle2 className="h-4 w-4" /> : customer.name.charAt(0)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className={cn('font-medium text-sm', !isAvailable && 'text-neutral-400 dark:text-neutral-500')}>
                                                            {customer.name}
                                                        </p>
                                                        {hasSigned && (
                                                            <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 h-4">
                                                                서명완료
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                        {customer.phone || '전화번호 없음'}
                                                        {(customer as { email?: string }).email && ` · ${(customer as { email?: string }).email}`}
                                                    </p>
                                                    {!isAvailable && (
                                                        <p className="text-xs text-danger-500 mt-0.5 flex items-center gap-1">
                                                            <AlertCircle className="h-3 w-3" />
                                                            {unavailableReason}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                    {customer.petCount}마리
                                                </div>
                                            </label>
                                        );
                                    })}
                                    {/* 로딩 인디케이터 */}
                                    {isFetchingNextCustomerPage && (
                                        <div className="flex justify-center py-2 border-t">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        </div>
                                    )}
                                    {hasNextCustomerPage && !isFetchingNextCustomerPage && (
                                        <div className="p-2 text-center border-t">
                                            <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                스크롤하여 더 보기
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* 선택 불가 고객 안내 */}
                        {allCustomers.length > availableCustomers.length && (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {sendMethod === 'sms' ? '전화번호' : '이메일'}가 없는 고객 {allCustomers.length - availableCustomers.length}명은 선택할 수 없습니다.
                            </p>
                        )}
                    </div>

                    {/* 발송 안내 */}
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <p className="text-sm text-blue-700">
                            <span className="font-medium">💡 안내:</span> 선택된 고객에게 동의서 서명 요청이 발송됩니다.
                            고객은 알림을 통해 동의서를 확인하고 서명할 수 있습니다.
                        </p>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={handleClose}>
                        취소
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={!selectedTemplateId || selectedCustomerIds.length === 0}
                        className="gap-2"
                    >
                        <Send className="h-4 w-4" />
                        {selectedCustomerIds.length > 0
                            ? `${selectedCustomerIds.length}명에게 발송`
                            : '발송'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== Version History Modal ====================
interface VersionHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    template: ConsentTemplate | null;
    versionHistory?: ConsentTemplateVersion[];
    onSetActiveVersion?: (templateId: string, version: number) => void;
    storeInfo?: StoreInfo;
    groupId?: number;
}

export function VersionHistoryModal({ isOpen, onClose, template, versionHistory = [], onSetActiveVersion, storeInfo, groupId }: VersionHistoryModalProps) {
    const [selectedVersionNumber, setSelectedVersionNumber] = useState<number | null>(null);

    // 선택된 버전의 상세 정보 조회
    const { data: versionDetailData, isLoading: isLoadingVersionDetail } = useGetVersion({
        request: {
            groupId: groupId || 0,
            templateId: template?.id ? Number(template.id) : 0,
            versionNumber: selectedVersionNumber || template?.currentVersion || 1,
        },
        options: {
            enabled: isOpen && !!groupId && !!template?.id,
        },
    });

    // 버전 상세 정보를 로컬 타입으로 변환
    const selectedVersionDetail = useMemo(() => {
        if (!versionDetailData) return null;
        const data = versionDetailData as {
            title: string;
            versionNumber?: number;
            contentHtml?: string;
            questions?: Array<{
                id?: number;
                questionText?: string;
                type?: string;
                options?: string[];
                required?: boolean;
            }>;
            createdAt?: string;
            changeNote?: string;
            fileUrl?: string;
            contentType?: string;
        };
        // 질문 타입 변환 함수
        const convertQuestionType = (apiType?: string): 'single' | 'multiple' | 'text' => {
            if (apiType === 'SINGLE_CHOICE') return 'single';
            if (apiType === 'MULTI_CHOICE') return 'multiple';
            return 'text';
        };


        return {
            title: data.title || '',
            version: data.versionNumber || 1,
            content: data.contentHtml || '',
            questions: (data.questions || []).map((q) => ({
                id: String(q.id || ''),
                name: q.questionText || '',
                type: convertQuestionType(q.type),
                options: (q.options || []).map((o) => ({
                    id: String(o || ''),
                    value: o || '',
                })),
                isRequired: q.required || false,
            })),
            createdAt: data.createdAt || '',
            changeNote: data.changeNote,
            fileUrl: data.fileUrl,
            isFileUploaded: !!data.fileUrl && data.contentType !== 'HTML',
            fileType: data.contentType === 'PDF' ? 'pdf' as const : data.contentType === 'IMAGE' ? 'image' as const : null,
        };
    }, [versionDetailData]);



    // API에서 가져온 versionHistory를 사용하고, 없으면 template.versionHistory 사용
    const versions = useMemo(() => {
        const historyData = versionHistory.length > 0 ? versionHistory : (template?.versionHistory || []);
        return [...historyData].sort((a, b) => b.version - a.version);
    }, [versionHistory, template]);

    // 현재 선택된 버전이 활성 버전인지 확인
    const currentActiveVersion = template?.activeVersion || template?.currentVersion || 1;
    const displayedVersion = selectedVersionNumber || template?.currentVersion || 1;
    const isDisplayedVersionActive = displayedVersion === currentActiveVersion;

    // 이 버전을 사용 버튼 핸들러
    const handleSetActiveVersion = () => {
        if (!template || !displayedVersion) return;
        onSetActiveVersion?.(String(template.id), displayedVersion);
    };

    // 모달 닫기 시 초기화
    const handleClose = () => {
        setSelectedVersionNumber(null);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <History className="h-5 w-5 text-orange-500" />
                        버전 이력 - {template?.name}
                    </DialogTitle>
                    <DialogDescription>
                        동의서 양식의 이전 버전을 확인하고 사용할 버전을 선택할 수 있습니다.
                    </DialogDescription>
                </DialogHeader>

                {template && (
                    <div className="flex-1 grid grid-cols-[250px_1fr] gap-4 overflow-hidden mt-4">
                        {/* 왼쪽: 버전 목록 */}
                        <div className="border rounded-lg overflow-hidden flex flex-col">
                            <div className="bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 border-b">
                                <h4 className="font-semibold text-sm">버전 목록</h4>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5">
                                    사용 중: v{currentActiveVersion}
                                </p>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                {versions.map((version) => {
                                    const isLatest = version.version === template.currentVersion;
                                    const isActive = version.version === currentActiveVersion;
                                    const isSelected = selectedVersionNumber === version.version || (!selectedVersionNumber && isLatest);

                                    return (
                                        <div
                                            key={version.version}
                                            className={cn(
                                                'p-3 border-b cursor-pointer hover:bg-neutral-50 dark:bg-neutral-800 dark:hover:bg-neutral-800 transition-colors',
                                                isSelected && 'bg-orange-50 dark:bg-orange-950/30 border-l-4 border-l-orange-500'
                                            )}
                                            onClick={() => setSelectedVersionNumber(isLatest ? null : version.version)}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium text-sm flex items-center gap-1 flex-wrap">
                                                        v{version.version}
                                                        {isLatest && (
                                                            <Badge className="bg-blue-100 text-blue-700 text-[10px]">최신</Badge>
                                                        )}
                                                        {isActive && (
                                                            <Badge className="bg-success-100 text-success-700 text-[10px]">사용 중</Badge>
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5">
                                                        {new Date(new Date(version.createdAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { timeZone: 'UTC' })}
                                                    </p>
                                                    {version.changeNote && (
                                                        <p className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5 truncate" title={version.changeNote}>
                                                            {version.changeNote}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {versions.length === 0 && (
                                    <div className="p-4 text-center text-neutral-400 dark:text-neutral-500">
                                        <Clock className="h-8 w-8 mx-auto mb-2" />
                                        <p className="text-sm">버전 이력이 없습니다</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 오른쪽: 버전 내용 미리보기 */}
                        <div className="border rounded-lg overflow-hidden flex flex-col">
                            <div className="bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 border-b flex items-center justify-between">
                                <div>
                                    <h4 className="font-semibold text-sm flex items-center gap-2">
                                        v{displayedVersion} 내용
                                        {isDisplayedVersionActive && (
                                            <Badge className="bg-success-100 text-success-700 text-[10px]">사용 중</Badge>
                                        )}
                                        {isLoadingVersionDetail && (
                                            <Loader2 className="h-4 w-4 animate-spin text-neutral-400 dark:text-neutral-500" />
                                        )}
                                    </h4>
                                    {(selectedVersionDetail?.changeNote || versions.find(v => v.version === displayedVersion)?.changeNote) && (
                                        <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5">
                                            변경사항: {selectedVersionDetail?.changeNote || versions.find(v => v.version === displayedVersion)?.changeNote}
                                        </p>
                                    )}
                                </div>
                                {!isDisplayedVersionActive && onSetActiveVersion && (
                                    <Button
                                        size="sm"
                                        onClick={handleSetActiveVersion}
                                        className="gap-1"
                                    >
                                        <CheckCircle2 className="h-3 w-3" />
                                        이 버전을 사용
                                    </Button>
                                )}
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 bg-neutral-100 dark:bg-neutral-800/50">
                                <div className="bg-white dark:bg-neutral-900 rounded-lg shadow p-6">
                                    {/* 동의서 제목 */}
                                    <h3 className="text-xl font-bold text-center mb-6 pb-4 border-b-2">
                                        {selectedVersionDetail?.title}
                                    </h3>

                                    {/* 매장정보 + 보호자/반려동물 정보 */}
                                    <div className="mb-6 grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">매장정보</h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-20 shrink-0">매장명:</span> <span>{storeInfo?.name || '-'}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">주소:</span> <span className="break-all">{storeInfo?.address || '-'}</span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">전화번호:</span> <span>{storeInfo?.phone || '-'}</span></div>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">보호자 및 반려동물 <span className="text-danger-500">*</span></h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                                                <div className="flex"><span className="font-medium w-20 shrink-0">보호자:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">연락처:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">반려동물:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                                <div className="flex"><span className="font-medium w-20 shrink-0">종/품종:</span> <span className="flex-1 border-b border-neutral-300 dark:border-neutral-600 min-h-[1.25rem]"></span></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 추가 정보 (질문 항목) */}
                                    {/* {(selectedVersionDetail?.questions || template.questions)?.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">추가 정보</h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-3">
                                                {(selectedVersionDetail?.questions || template.questions).map((q) => (
                                                    <div key={q.id} className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded">
                                                        <div className="font-medium">
                                                            {q.name || '질문명'}
                                                            {q.isRequired && <span className="text-danger-500 ml-1">*</span>}
                                                            {q.type === 'multiple' && <span className="text-xs text-blue-500 ml-1">(복수 선택)</span>}
                                                        </div>
                                                        {q.type === 'single' && q.options.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {q.options.map((o) => (
                                                                    <span key={o.id} className="px-3 py-1 bg-white dark:bg-neutral-900 border rounded-full text-xs">
                                                                        ○ {o.value || '옵션'}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {q.type === 'multiple' && q.options.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {q.options.map((o) => (
                                                                    <span key={o.id} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                                                                        ☐ {o.value || '옵션'}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {q.type === 'text' && (
                                                            <div className="mt-2 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900">
                                                                <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                <div className="h-6"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )} */}



                                    {(selectedVersionDetail?.questions || template.questions)?.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold mb-3 text-neutral-900">추가 정보</h4>
                                            <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-3">
                                                {(selectedVersionDetail?.questions || template.questions).map((q) => (
                                                    <div key={q.id} className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded">
                                                        <div className="font-medium">
                                                            {q.name || '질문명'}
                                                            {q.isRequired && <span className="text-danger-500 ml-1">*</span>}
                                                            {q.type === 'multiple' && <span className="text-xs text-blue-500 ml-1">(복수 선택)</span>}
                                                        </div>
                                                        {q.type === 'single' && q.options.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {q.options.map((o) => (
                                                                    <span key={o.id} className="px-3 py-1 bg-white dark:bg-neutral-900 border rounded-full text-xs">
                                                                        ○ {o.value || '옵션'}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {q.type === 'multiple' && q.options.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {q.options.map((o) => (
                                                                    <span key={o.id} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
                                                                        ☐ {o.value || '옵션'}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {q.type === 'text' && (
                                                            <div className="mt-2 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-900">
                                                                <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                <div className="h-6 border-b border-neutral-200 dark:border-neutral-700"></div>
                                                                <div className="h-6"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* 동의서 본문 */}
                                    <div className="mb-6">
                                        {(selectedVersionDetail?.isFileUploaded && selectedVersionDetail?.fileUrl) || (template.isFileUploaded && template.fileUrl) ? (
                                            <div className="space-y-3">
                                                {(selectedVersionDetail?.fileType || template.fileType) === 'image' ? (
                                                    <img
                                                        src={selectedVersionDetail?.fileUrl || template.fileUrl || ''}
                                                        alt={template.name}
                                                        className="w-full max-h-96 object-contain rounded border"
                                                    />
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center py-8 bg-neutral-100 dark:bg-neutral-800 rounded">
                                                        <FileType2 className="h-12 w-12 text-red-500 mb-2" />
                                                        <span className="text-sm text-neutral-600 dark:text-neutral-300">{template.fileName || 'PDF 파일'}</span>
                                                        <span className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">서명 요청 시 PDF 내용이 표시됩니다</span>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (selectedVersionDetail?.content || template.content) ? (
                                            <div
                                                className="prose prose-sm max-w-none
                                                    [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6
                                                    [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5
                                                    [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-4
                                                    [&_p]:mb-3 [&_p]:leading-relaxed
                                                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                                                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                                                    [&_li]:mb-1
                                                    [&_strong]:font-bold
                                                    [&_em]:italic
                                                    [&_u]:underline
                                                    [&_s]:line-through
                                                    [&_hr]:my-4 [&_hr]:border-neutral-300 dark:border-neutral-600
                                                    text-sm text-neutral-700 dark:text-neutral-200"
                                                dangerouslySetInnerHTML={{
                                                    __html: selectedVersionDetail?.content || template.content || ''
                                                }}
                                            />
                                        ) : (
                                            <div className="p-8 text-center text-neutral-400 dark:text-neutral-500 border-2 border-dashed rounded-lg">
                                                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">동의서 내용이 없습니다.</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* 서명란 */}
                                    <div className="pt-6 border-t-2 mt-6">
                                        <div className="text-right">
                                            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                                                위 내용을 확인하였으며, 동의합니다.
                                            </p>
                                            <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                                {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                            <p className="text-lg font-semibold mt-2">
                                                <span className="text-orange-500">[보호자명]</span> (인)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={handleClose}>
                        닫기
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
